// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';
import { AudioManager } from '../core/AudioManager.js';
import { InputManager } from '../core/InputManager.js';
import { MaterialLibrary, type TankPalette } from '../render/MaterialLibrary.js';
import { ModelFactory, type TankModel } from '../render/ModelFactory.js';
import { VfxSystem } from '../render/VfxSystem.js';
import { VictoryFX } from '../render/VictoryFX.js';
import { FogOfWar } from '../render/FogOfWar.js';
import { TrailSystem } from '../render/TrailSystem.js';
import { SceneStyler } from '../render/SceneStyler.js';
import { UIController, type RadarData } from '../ui/UIController.js';
import { BUFF_FLAG_DRONE, BUFF_FLAG_OVERDRIVE, BUFF_FLAG_RAPID, MATCH_RULES, SNAPSHOT_INTERVAL_S } from '../net/protocol.js';
import type { CombatEvent, MatchResult, TankSnapshot, TeamId } from '../net/protocol.js';
import type { NetClient } from '../net/NetClient.js';
import { RemoteBuffer } from '../net/RemoteBuffer.js';
import { DIFFICULTY_TUNING, LEVELS, PVP_ARENA, totalEnemies } from './levels.js';
import { PICKUP_INFO, PICKUP_KINDS, PICKUP_TUNING, PICKUP_WEIGHTS } from './pickup-info.js';
import type {
  Difficulty,
  EnemyKind,
  GameMode,
  GamePhase,
  GameSelection,
  GameSnapshot,
  HazardKind,
  LevelConfig,
  ObstacleKind,
  PickupKind,
  PvpContext,
  PvpHudState,
  ResultSummary,
  Team,
  Vec2,
} from './types.js';

/** UI 联机契约（UIController 由并行代理实现；未就绪时可选调用静默跳过）。 */
interface PvpUiBridge {
  setMatchHud?(hud: PvpHudState | null): void;
  showMatchResult?(result: MatchResult, selfId: string, selfTeam: TeamId): void;
}

interface TankEntity {
  id: number;
  team: Team;
  kind: EnemyKind | 'player';
  playerIndex: number;
  model: TankModel;
  velocity: any;
  bodyAngle: number;
  turretAngle: number;
  health: number;
  maxHealth: number;
  lives: number;
  radius: number;
  speed: number;
  fireCooldown: number;
  fireInterval: number;
  projectileSpeed: number;
  damage: number;
  alive: boolean;
  respawnTimer: number;
  invulnerable: number;
  shieldTimer: number;
  rapidTimer: number;
  overdriveTimer: number;
  droneTimer: number;
  droneCooldown: number;
  hazardTimer: number;
  inputIntensity: number;
  speedRatio: number;
  handlingLean: number;
  handlingPitch: number;
  recoil: number;
  dustTimer: number;
  hitFlash: number;
  spawnPoint: Vec2;
  aiBlocked: number;
  aiTurnDirection: number;
  aiRetarget: number;
  targetId: number | null;
  bossSummonMask: number;
  bossBurstTimer: number;
  /** Spawn heading in radians; respawns restore it and PvP spawns face the arena center. */
  spawnAngle: number;
  /** Logical pose before the latest fixed step, retained for render interpolation. */
  prevX: number;
  prevZ: number;
  prevBody: number;
  prevTurret: number;
  /** Logical position after fixed stepping, used to restore the shared render object. */
  curX: number;
  curZ: number;
  /** PvP 远程玩家标记：由快照驱动，不参与本地输入/AI 模拟与本地扣血。 */
  remote?: boolean;
  /** 协议层 playerId（RemoteBuffer 键、hit/died 归因）。 */
  remotePlayerId?: string;
  /** PvP 头顶血条（仅远程坦克创建；fill 左锚定，scale.x 按血量比例伸缩）。 */
  hpBar?: { root: any; fill: any; fullWidth: number };
}

interface ProjectileEntity {
  id: number;
  team: Team;
  ownerId: number;
  root: any;
  velocity: any;
  radius: number;
  damage: number;
  life: number;
  bounces: number;
  age: number;
  strong: boolean;
  poolKey: ProjectilePoolKey;
  /** Position before/after the latest fixed step; newly fired shells start with prev=cur. */
  prevX: number;
  prevZ: number;
  curX: number;
  curZ: number;
  /** PvP 迷雾标记：敌方炮弹出膛点不可见时整颗隐藏（不暴露射手位置）。 */
  fogHidden?: boolean;
}

type ProjectilePoolKey = 'player-normal' | 'enemy-normal' | 'enemy-strong';

interface ObstacleEntity {
  id: number;
  kind: ObstacleKind;
  root: any;
  x: number;
  z: number;
  width: number;
  depth: number;
  rotation: number;
  health: number;
  maxHealth: number;
  active: boolean;
}

interface PickupEntity {
  id: number;
  kind: PickupKind;
  root: any;
  orbiters: any[];
  core: any;
  x: number;
  z: number;
  life: number;
  phase: number;
  /** 渲染插值：自旋角/浮沉高度的上一固定步值与逻辑值。 */
  prevRotY: number;
  prevY: number;
  curRotY: number;
  curY: number;
}

interface PickupSpotEntity {
  id: number;
  root: any;
  x: number;
  z: number;
  weight: number;
  kinds: PickupKind[];
}

interface HazardEntity {
  id: number;
  kind: HazardKind;
  root: any;
  x: number;
  z: number;
  radius: number;
  strength: number;
  phase: number;
}

interface MineEntity {
  id: number;
  ownerId: number;
  /** 布雷者队伍（布雷时解析；找不到记 null = 对任何触发方有效，兼容布雷者离场）。 */
  ownerTeam: Team | null;
  /** 布雷者协议层 playerId（PvP died 归因用；PvE 恒 null）。 */
  ownerPlayerId: string | null;
  root: any;
  x: number;
  z: number;
  radius: number;
  life: number;
  armed: number;
  phase: number;
  /** 渲染插值：自旋角的上一固定步值与逻辑值。 */
  prevRotY: number;
  curRotY: number;
}

interface CoreEntity {
  root: any;
  crystal: any;
  rings: any[];
  shield: any;
  x: number;
  z: number;
  health: number;
  maxHealth: number;
  invulnerable: number;
  pulse: number;
}

interface WaveRuntime {
  index: number;
  queue: EnemyKind[];
  spawnTimer: number;
  transitionTimer: number;
  completionTimer: number;
}

const FIXED_STEP = 1 / 60;
const MAX_FRAME_DELTA = 0.1;
const MAX_CATCH_UP_STEPS = 3;
/** PvP 拾取物刷新槽位周期（ms）：58×42 大地图密度调校，比 PvE（均值 7.2s）略密（2026-07-26 加密一档，补给争夺更频繁）。 */
const PVP_PICKUP_SLOT_MS = 5000;
const UP = new THREE.Vector3(0, 1, 0);
const GROUND_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const TEMP_VECTOR = new THREE.Vector3();
const TEMP_VECTOR_B = new THREE.Vector3();
const TEMP_VECTOR_C = new THREE.Vector3();
const TEMP_QUATERNION = new THREE.Quaternion();

function damp(current: number, target: number, smoothing: number, delta: number): number {
  return THREE.MathUtils.lerp(current, target, 1 - Math.exp(-smoothing * delta));
}

function dampAngle(current: number, target: number, smoothing: number, delta: number): number {
  let difference = (target - current + Math.PI) % (Math.PI * 2) - Math.PI;
  if (difference < -Math.PI) difference += Math.PI * 2;
  return current + difference * (1 - Math.exp(-smoothing * delta));
}

/** 最短弧角度插值（bodyAngle/turretAngle 环绕安全）。 */
function lerpAngle(from: number, to: number, t: number): number {
  let difference = (to - from + Math.PI) % (Math.PI * 2) - Math.PI;
  if (difference < -Math.PI) difference += Math.PI * 2;
  return from + difference * t;
}

/** 字符串 → 32 位种子（FNV-1a，PvP 共享世界种子用）。 */
function hashSeed(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32 确定性 PRNG：PvP 共享世界（道具刷新）按「种子基 + 槽号」派生独立种子，两端一致；PvE 直接用 Math.random。 */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function angleTo(fromX: number, fromZ: number, toX: number, toZ: number): number {
  return Math.atan2(toX - fromX, -(toZ - fromZ));
}

function forwardFromAngle(angle: number, target = new THREE.Vector3()): any {
  return target.set(Math.sin(angle), 0, -Math.cos(angle));
}

function distanceSq2D(a: { x: number; z: number }, b: { x: number; z: number }): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
}

export class TankGame {
  readonly canvas: HTMLCanvasElement;
  readonly ui: UIController;
  readonly input: InputManager;
  readonly audio: AudioManager;

  private readonly renderer: any;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 180);
  private readonly raycaster = new THREE.Raycaster();
  private readonly clock = new THREE.Clock();
  private readonly materials = new MaterialLibrary();
  private readonly models = new ModelFactory(this.materials);
  private readonly vfx: VfxSystem;
  /** 炮弹拖尾 + 履带印痕（实例长驻，clearWorld 只 clear 不 dispose，同 vfx 惯例）。 */
  private readonly trails: TrailSystem;
  /** 场景氛围造型（天空/尘埃/草丛；跨关复用，setupEnvironment 每关 apply 重配）。 */
  private readonly styler: SceneStyler;
  private readonly worldRoot = new THREE.Group();
  private readonly gameplayRoot = new THREE.Group();
  private readonly decorRoot = new THREE.Group();
  private readonly dynamicRoot = new THREE.Group();
  private readonly vfxRoot = new THREE.Group();
  private readonly cameraTarget = new THREE.Vector3();
  private readonly desiredCameraTarget = new THREE.Vector3();
  private readonly aimPoint = new THREE.Vector3();
  private readonly projectilePool: Record<ProjectilePoolKey, ProjectileEntity[]> = {
    'player-normal': [],
    'enemy-normal': [],
    'enemy-strong': [],
  };
  private readonly activeTanksScratch: TankEntity[] = [];
  private readonly syncTanksScratch: TankEntity[] = [];
  /** updateCamera 专用 scratch（该热路径此前每帧 .filter 分配 1-2 个数组，是全文件最后一处漏网）。 */
  private readonly cameraTanksScratch: TankEntity[] = [];

  private hemisphere: any;
  private keyLight: any;
  private fillLight: any;
  private rimLight: any;
  private coreLight: any;
  private menuTanks: TankModel[] = [];
  private menuTime = 0;
  private phase: GamePhase = 'menu';
  private selection: GameSelection = { mode: 'single', levelIndex: 0, difficulty: 'normal' };
  private level: LevelConfig = LEVELS[0];
  private players: TankEntity[] = [];
  private enemies: TankEntity[] = [];
  /**
   * Dead PvE enemies waiting for reclamation at the start of the next frame.
   * destroyTank often runs while fixedUpdate iterates this.enemies, so immediate
   * splicing would corrupt iteration. Deferring is visually equivalent because
   * the model is hidden as soon as the enemy dies.
   */
  private readonly enemyReclaimQueue: TankEntity[] = [];
  private projectiles: ProjectileEntity[] = [];
  private obstacles: ObstacleEntity[] = [];
  private pickups: PickupEntity[] = [];
  private pickupSpots: PickupSpotEntity[] = [];
  private hazards: HazardEntity[] = [];
  private mines: MineEntity[] = [];
  private core: CoreEntity | null = null;
  private wave: WaveRuntime = { index: -1, queue: [], spawnTimer: 0, transitionTimer: 1.5, completionTimer: 0 };
  private pickupSpawnTimer = 0;
  private entitySerial = 0;
  private accumulator = 0;
  private score = 0;
  private combo = 1;
  private maxCombo = 1;
  private comboTimer = 0;
  private defeated = 0;
  private elapsed = 0;
  private frameCounter = 0;
  private fps = 60;
  private fpsAccumulator = 0;
  private fpsFrames = 0;
  private shake = 0;
  private debugVisible = false;
  private debugPanelWasVisible = false;
  private lastDebugUpdate = 0;
  private contextLost = false;
  private loopEnabled = true;
  private startGamePending = false;
  private audioUnlockFailures = 0;
  private resultCommitted = false;
  private lastHudUpdate = 0;
  private lastRadarUpdate = 0;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private lowSpec = navigator.webdriver;
  /** mobileLike 缓存：值只会在 resize/orientationchange 时变化；此前 getter 每次读都 matchMedia（实测 ~300 次/秒 + 布局刷新风险）。 */
  private mobileLikeValue = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 760;
  private shadowsEnabled = false;
  private renderScale = 1;
  private lowFpsWindows = 0;
  private highFpsWindows = 0;
  private adaptiveShadowDrop = false;
  private droppedSimulationFrames = 0;
  private lastShaderPrewarmMs = 0;
  private shaderPrewarmFailures = 0;
  /** 触屏瞄准摇杆松开后的保持剩余时间（秒），窗口期内炮管维持当前角度、不自动索敌。 */
  private touchAimHold = 0;
  /** 渲染插值：本帧 syncRenderTransforms 已改写 root 变换，render 后需恢复逻辑坐标。 */
  private renderSyncActive = false;

  // -------------------------------------------------------------------------
  // PvP 联机状态（设计文档 §7；仅 mode==='pvp' 时有效）
  // -------------------------------------------------------------------------
  /** 当前对局上下文（match_start 注入，quitToMenu/开新局时清空）。 */
  private pvpContext: PvpContext | null = null;
  /** 当前会话网络层（由 OnlinePvP 协调器持有与订阅，这里只负责发送）。 */
  private pvpNet: NetClient | null = null;
  /** 远程玩家快照插值缓冲（队友 + 对手）。 */
  private readonly remoteBuffer = new RemoteBuffer();
  /** 战争迷雾（pvp 开局创建，clearWorld 释放）。 */
  private fog: FogOfWar | null = null;
  /** 本地比赛时钟原点（performance.now() 域，match_start.startsAt 换算而来）。 */
  private pvpMatchStartAt = 0;
  /** PvP 共享世界种子基（`${roomCode}:${startsAt}`，服务器时钟域两端一致）；拾取物按刷新槽位派生独立种子。 */
  private pvpSeedBase = '';
  /** PvP 拾取物刷新槽位游标（已处理的最大槽号；-1 = 未处理，开局 0 号槽即刷第一个）。 */
  private pvpPickupSlotHandled = -1;
  /** 远程玩家最新增益标记位（handlePvpRelay 缓存；RemoteBuffer 不透传可选 buffs 字段）。 */
  private readonly pvpRemoteBuffs = new Map<string, number>();
  /** 倒计时最后展示的秒数（-1 = 未开播/已结束）。 */
  private pvpCountdownShown = -1;
  /** 本地比分缓存 [alpha, bravo]（仅用于 HUD 即时显示，结算以 match_end 为准）。 */
  private pvpScore: [number, number] = [0, 0];
  /** 骤死加时已播报标记（服务端平分进骤死不发消息，客户端按时钟推断）。 */
  private pvpSuddenDeathAnnounced = false;
  /** 已收到的对局结果（match_end），非 null 表示本局已结束。 */
  private pvpResult: MatchResult | null = null;
  /** playerId → 昵称（击杀播报用）。 */
  private pvpNames = new Map<string, string>();
  /** 最近一次对我造成伤害的攻击者 playerId（died.killerId 归因）。 */
  private pvpLastAttacker: string | null = null;
  /** 本局是否已播报首杀（FIRST BLOOD，全局一次）。 */
  private pvpFirstBloodDone = false;
  /** playerId → 当前连杀数（死亡清零；≥3 被终结时播报）。 */
  private readonly pvpKillStreaks = new Map<string, number>();
  /** 快照发送节流（间隔 = protocol.SNAPSHOT_INTERVAL_S，当前 50ms ≈ 20Hz）。 */
  private pvpSnapshotTimer = 0;
  /** setMatchHud 刷新节流（~250ms）。 */
  private pvpHudTimer = 0;
  /** 迷雾更新节流（每 6 个固定步 ≈ 10Hz）。 */
  private pvpFogStep = 0;
  /** PvP 结算胜利演出（match_end 时启动，clearWorld 时 dispose 释放）。 */
  private victoryFx: VictoryFX | null = null;
  /** PvP 暂停屏遮罩（本地模拟不暂停，仅作「退出对战」入口）。 */
  private pvpPauseOverlay = false;
  /** 联机对局中「返回主菜单」的回调（OnlinePvP 注入：leave_room 触发判负 + 断连）。 */
  pvpQuitHandler: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement, ui: UIController) {
    this.canvas = canvas;
    this.ui = ui;
    this.input = new InputManager(canvas);
    this.audio = new AudioManager();
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: !this.lowSpec,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    const gl = this.renderer.getContext();
    const debugRenderer = gl.getExtension('WEBGL_debug_renderer_info');
    const rendererName = debugRenderer ? String(gl.getParameter(debugRenderer.UNMASKED_RENDERER_WEBGL)) : '';
    this.lowSpec = this.lowSpec || /swiftshader|llvmpipe|software/i.test(rendererName);
    this.shadowsEnabled = this.shouldUseRealtimeShadows();
    this.renderer.shadowMap.enabled = this.shadowsEnabled;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene.add(this.worldRoot);
    this.worldRoot.add(this.decorRoot, this.gameplayRoot, this.dynamicRoot, this.vfxRoot);
    this.vfx = new VfxSystem(this.vfxRoot, this.materials, this.lowSpec ? 0.42 : this.mobileLike ? 0.62 : 0.86);
    this.trails = new TrailSystem(this.vfxRoot, this.lowSpec ? 0.5 : this.mobileLike ? 0.7 : 1);
    this.createLighting();
    this.styler = new SceneStyler(this.scene, this.materials, this.lowSpec ? 0.5 : this.mobileLike ? 0.7 : 1);
    this.bindUI();
    this.bindLifecycle();
    this.buildMenuScene();
    this.resize();
    this.exposeDiagnostics();
    this.clock.start();
    requestAnimationFrame(this.loop);
  }

  get currentPhase(): GamePhase {
    return this.phase;
  }

  /** 是否处于 PvP 对局上下文（协调器据此决定 match_end 路由）。 */
  get pvpActive(): boolean {
    return this.pvpContext !== null;
  }

  async startGame(selection: GameSelection): Promise<void> {
    // PvP 对局由 OnlinePvP 协调器经 startPvpGame 进入，常规入口忽略 pvp 选择。
    if (selection.mode === 'pvp' || this.startGamePending) return;
    this.startGamePending = true;
    try {
      this.requestAudioUnlock();
      this.selection = {
        mode: selection.mode,
        levelIndex: Math.max(0, Math.min(LEVELS.length - 1, selection.levelIndex)),
        difficulty: selection.difficulty,
      };
      this.level = LEVELS[this.selection.levelIndex];
      this.resetPvpState();
      this.resultCommitted = false;
      this.score = 0;
      this.combo = 1;
      this.maxCombo = 1;
      this.comboTimer = 0;
      this.defeated = 0;
      this.elapsed = 0;
      this.accumulator = 0;
      this.shake = 0;
      this.input.clear();
      this.clearWorld();
      this.buildLevel();
      await this.prewarmSceneShaders();
      this.phase = 'playing';
      this.ui.showGame(this.selection.mode);
      this.ui.showBanner(`关卡 ${this.level.id}`, this.level.objective);
      this.audio.play('wave');
      this.updateHud(true);
    } finally {
      this.startGamePending = false;
    }
  }

  /**
   * PvP 对局入口：由 OnlinePvP 协调器在 match_start 时调用。
   *
   * @param context 对局上下文（房间码/己方队伍/成员列表）。
   * @param net 已连接的网络层（本方法只负责发送，接收由协调器订阅后转发）。
   * @param startsAt 约定的开局时刻（服务器时钟 ms），用 net.serverNow() 换算倒计时。
   */
  async startPvpGame(context: PvpContext, net: NetClient, startsAt: number): Promise<void> {
    this.requestAudioUnlock();
    this.selection = { mode: 'pvp', levelIndex: 0, difficulty: 'normal' };
    this.level = PVP_ARENA;
    this.resetPvpState();
    this.pvpContext = context;
    this.pvpNet = net;
    this.pvpNames = new Map(context.members.map((member) => [member.playerId, member.name]));
    // startsAt 为服务器时钟域，换算成本地 performance.now() 域的比赛时钟原点。
    this.pvpMatchStartAt = performance.now() + Math.max(0, startsAt - net.serverNow());
    // 共享世界种子基：两端一致（房间码 + 服务器时钟域开局时刻），拾取物刷新按槽位派生独立种子（见 updatePvpPickupSpawn）。
    this.pvpSeedBase = `${context.roomCode}:${startsAt}`;
    this.pvpPickupSlotHandled = -1;
    this.pvpCountdownShown = -1;
    this.resultCommitted = false;
    this.score = 0;
    this.combo = 1;
    this.maxCombo = 1;
    this.comboTimer = 0;
    this.defeated = 0;
    this.elapsed = 0;
    this.accumulator = 0;
    this.shake = 0;
    this.input.clear();
    this.clearWorld();
    this.buildLevel();
    await this.prewarmSceneShaders();
    this.phase = 'playing';
    this.ui.showGame('pvp');
    this.ui.showBanner('ONLINE PVP', '对战倒计时');
    this.audio.play('wave');
    this.pvpUi().setMatchHud?.(this.pvpHudState());
    this.updateHud(true);
  }

  /**
   * Audio is optional presentation. Browser autoplay policy or a device-specific
   * AudioContext failure must never block world construction or simulation.
   */
  private requestAudioUnlock(): void {
    void this.audio.unlock().catch(() => {
      this.audioUnlockFailures += 1;
    });
  }

  /**
   * Compile scene programs and submit one hidden warm-up render before revealing
   * the battlefield. The render uploads geometry and textures that compileAsync
   * intentionally leaves for the first draw. Pausing the production loop prevents
   * an ordinary render from racing this preparation.
   */
  private async prewarmSceneShaders(): Promise<void> {
    const startedAt = performance.now();
    const loopWasEnabled = this.loopEnabled;
    this.loopEnabled = false;
    try {
      const supportsParallelCompile = Boolean(this.renderer.getContext().getExtension('KHR_parallel_shader_compile'));
      if (supportsParallelCompile && typeof this.renderer.compileAsync === 'function') {
        await this.renderer.compileAsync(this.scene, this.camera);
      } else {
        this.renderer.compile(this.scene, this.camera);
      }
      this.renderer.render(this.scene, this.camera);
    } catch {
      this.shaderPrewarmFailures += 1;
    } finally {
      this.lastShaderPrewarmMs = performance.now() - startedAt;
      this.accumulator = 0;
      this.clock.getDelta();
      this.loopEnabled = loopWasEnabled;
    }
  }

  /** 清空 PvP 对局状态（开新局/回主菜单时调用；迷雾实体由 clearWorld 释放）。 */
  private resetPvpState(): void {
    this.pvpContext = null;
    this.pvpNet = null;
    this.pvpResult = null;
    this.pvpSeedBase = '';
    this.pvpPickupSlotHandled = -1;
    this.pvpRemoteBuffs.clear();
    this.pvpScore = [0, 0];
    this.pvpPauseOverlay = false;
    this.pvpSuddenDeathAnnounced = false;
    this.pvpLastAttacker = null;
    this.pvpFirstBloodDone = false;
    this.pvpKillStreaks.clear();
    this.pvpNames.clear();
    this.pvpCountdownShown = -1;
    this.pvpSnapshotTimer = 0;
    this.pvpHudTimer = 0;
    this.pvpFogStep = 0;
    this.remoteBuffer.clear();
    this.pvpUi().setMatchHud?.(null);
  }

  /** UI 联机契约桥（并行代理实现前，可选调用静默跳过）。 */
  private pvpUi(): PvpUiBridge {
    return this.ui as unknown as PvpUiBridge;
  }

  /** 胜利演出实例：clearWorld 会 dispose 置空，结算时按需重建（画质分档与 VfxSystem 对齐）。 */
  private ensureVictoryFx(): VictoryFX {
    if (!this.victoryFx) {
      this.victoryFx = new VictoryFX(this.vfxRoot, this.lowSpec ? 0.5 : this.mobileLike ? 0.7 : 1);
    }
    return this.victoryFx;
  }

  /** 按绝对队伍收集成员坦克的世界坐标（坦克已下场时取其最后位置）。 */
  private pvpTeamPositions(context: PvpContext, team: TeamId): Array<{ x: number; z: number }> {
    const positions: Array<{ x: number; z: number }> = [];
    for (const member of context.members) {
      if (member.team !== team) continue;
      const tank = this.tankByPlayerId(member.playerId);
      if (tank) positions.push({ x: tank.model.root.position.x, z: tank.model.root.position.z });
    }
    return positions;
  }

  /** 本地比赛时钟（ms，从 match_start 起算；倒计时阶段为负值）。 */
  private pvpClockMs(): number {
    return performance.now() - this.pvpMatchStartAt;
  }

  /** 倒计时期间锁定本地输入。 */
  private pvpInputLocked(): boolean {
    return this.pvpContext !== null && performance.now() < this.pvpMatchStartAt;
  }

  /** 本端玩家坦克（pvp 下 playerIndex=0 且非 remote）。 */
  private selfTank(): TankEntity | null {
    return this.players.find((player) => !player.remote && player.playerIndex === 0) ?? null;
  }

  /** 按协议层 playerId 找本地坦克实体。 */
  private tankByPlayerId(playerId: string): TankEntity | null {
    for (const tank of [...this.players, ...this.enemies]) {
      if (tank.remotePlayerId === playerId) return tank;
    }
    return null;
  }

  /** 计算 PvP HUD 状态：常规时间到而未收 match_end 即判定进入骤死加时。 */
  private pvpHudState(): PvpHudState {
    const rule = MATCH_RULES[this.pvpContext?.mode ?? '1v1'];
    const clock = this.pvpClockMs();
    let remainingMs = rule.durationSec * 1000 - clock;
    let suddenDeath = false;
    if (remainingMs < 0) {
      // 服务端在平分进骤死时不发任何消息（见 room-do.ts onRegularTimeEnd），客户端据此推断。
      suddenDeath = true;
      remainingMs = rule.suddenDeathSec * 1000 - (clock - rule.durationSec * 1000);
    }
    return {
      remainingMs: Math.max(0, Math.round(remainingMs)),
      score: [this.pvpScore[0], this.pvpScore[1]],
      suddenDeath,
      // 击杀目标（MATCH_RULES），HUD 据此显示「目标 N 杀」。
      killTarget: rule.killTarget,
      rttMs: this.pvpNet?.rttMs ?? null,
    };
  }

  // -------------------------------------------------------------------------
  // PvP 运行时驱动（倒计时 / 快照发送 / 远程驱动 / 迷雾 / 对局 HUD）
  // -------------------------------------------------------------------------

  /** 倒计时播报：3/2/1 逐秒弹横幅，开局瞬间播报「开战」（pvpCountdownShown 记录已播秒数防重复）。 */
  private updatePvpCountdown(): void {
    const clock = this.pvpClockMs();
    if (clock < 0) {
      const sec = Math.ceil(-clock / 1000);
      if (sec !== this.pvpCountdownShown && sec <= 3) {
        this.pvpCountdownShown = sec;
        this.ui.showBanner(String(sec), '对战倒计时');
        this.audio.play('ui');
      }
      return;
    }
    if (this.pvpCountdownShown !== 0) {
      this.pvpCountdownShown = 0;
      this.ui.showBanner('开战', '击毁敌方坦克！');
      this.audio.play('wave');
    }
  }

  /**
   * PvP 快照发送：playing 且过了倒计时锁定期后，按 SNAPSHOT_INTERVAL_S（50ms ≈ 20Hz）
   * 广播自己坦克状态。死亡中也照常发（alive=false，坐标为最后位置），远端据此播爆炸并等待重生快照。
   * 调用点在 syncRenderTransforms 之前，保证读到的是逻辑坐标。
   */
  private sendPvpSnapshotThrottled(delta: number): void {
    if (!this.pvpContext || this.pvpResult || this.pvpInputLocked()) return;
    this.pvpSnapshotTimer += delta;
    if (this.pvpSnapshotTimer < SNAPSHOT_INTERVAL_S) return;
    this.pvpSnapshotTimer -= SNAPSHOT_INTERVAL_S;
    const self = this.selfTank();
    if (!self) return;
    this.pvpNet?.sendState({
      t: this.pvpClockMs(),
      x: self.model.root.position.x,
      z: self.model.root.position.z,
      body: self.bodyAngle,
      turret: self.turretAngle,
      vx: self.velocity.x,
      vz: self.velocity.z,
      hp: Math.max(0, self.health),
      maxHp: self.maxHealth,
      alive: self.alive,
      shield: self.shieldTimer > 0,
      // 增益标记位：远端据此复现速射/超载的开火视觉与环绕无人机（协议可选字段，旧端收到自动忽略）。
      buffs:
        (self.rapidTimer > 0 ? BUFF_FLAG_RAPID : 0) |
        (self.overdriveTimer > 0 ? BUFF_FLAG_OVERDRIVE : 0) |
        (self.droneTimer > 0 ? BUFF_FLAG_DRONE : 0),
    });
  }

  /**
   * PvP 渲染帧驱动（每渲染帧一次；非 PvP 直接返回）：
   * 倒计时播报 → 远程坦克快照驱动 → 迷雾 overlay 与实体可见性 → 对局 HUD（~4Hz）。
   * 调用点固定在 updateAmbientAnimations 之后（它会每帧重置 root.visible）。
   */
  private updatePvpFrame(delta: number): void {
    if (!this.pvpContext) return;
    this.updatePvpCountdown();
    this.syncRemoteTanks();
    if (this.fog) {
      this.fog.syncOverlay();
      this.applyFogVisibility();
    }
    this.pvpHudTimer += delta;
    if (this.pvpHudTimer >= 0.25) {
      this.pvpHudTimer = 0;
      if (!this.pvpResult) {
        const hud = this.pvpHudState();
        this.pvpUi().setMatchHud?.(hud);
        // 常规时间到而未收 match_end 即骤死加时（服务端不发消息，见 pvpHudState），首次进入播报一次。
        if (hud.suddenDeath && !this.pvpSuddenDeathAnnounced) {
          this.pvpSuddenDeathAnnounced = true;
          this.ui.toast('⚡', '骤死加时', '先杀者胜！');
        }
      }
    }
  }

  /**
   * 远程坦克快照驱动（渲染路径每帧）：RemoteBuffer 以本地比赛时钟采样插值，
   * 直接写入 model.root——远程坦克不参与固定步渲染插值（capturePrev/syncRenderTransforms 均已跳过它们），
   * 这里写完后保持 prev=cur 仅为字段语义完整。
   */
  private syncRemoteTanks(): void {
    const clock = this.pvpClockMs();
    // 与 updateAmbientAnimations 错峰复用 scratch（本方法在其之后调用，进入即清空）。
    const tanks = this.activeTanksScratch;
    tanks.length = 0;
    for (const player of this.players) if (player.remote) tanks.push(player);
    for (const enemy of this.enemies) if (enemy.remote) tanks.push(enemy);
    for (const tank of tanks) {
      const playerId = tank.remotePlayerId;
      if (!playerId) continue;
      const sample = this.remoteBuffer.sample(playerId, clock);
      if (!sample) continue; // 快照未到达：保持现状
      const root = tank.model.root;
      if (sample.alive && !tank.alive) {
        // 重生：恢复可见并直接瞬移到快照位置（不在死亡点与出生点之间拖影）。
        root.visible = true;
        root.position.set(sample.x, 0, sample.z);
      } else if (!sample.alive && tank.alive) {
        // 阵亡：爆炸演出只在状态翻转时播一次（died 事件先到时已置 dead，这里不会重复触发）。
        TEMP_VECTOR_C.set(sample.x, 0.45, sample.z);
        this.vfx.explosion(TEMP_VECTOR_C, tank.team === 'player' ? 0x66dfff : 0xff7358, 1);
        this.audio.play('explode', 0, { x: sample.x, z: sample.z });
      }
      tank.alive = sample.alive;
      root.position.x = sample.x;
      root.position.z = sample.z;
      if (!sample.alive) root.visible = false;
      // 与本地坦克相同的朝向同步方式（root.rotation.y = -bodyAngle，炮塔为相对角）。
      tank.bodyAngle = sample.body;
      tank.turretAngle = sample.turret;
      root.rotation.y = -sample.body;
      tank.model.turret.rotation.y = sample.body - sample.turret;
      // 快照速度写入 velocity，供远程队友的 inputIntensity/履带反馈推算。
      tank.velocity.set(sample.vx, 0, sample.vz);
      if (sample.hp < tank.health) tank.hitFlash = 0.14;
      tank.health = sample.hp;
      tank.maxHealth = sample.maxHp;
      // 头顶血条：按快照血量比例伸缩（左锚定）。
      if (tank.hpBar) {
        const ratio = sample.maxHp > 0 ? Math.min(1, Math.max(0, sample.hp / sample.maxHp)) : 0;
        tank.hpBar.fill.scale.x = Math.max(0.001, tank.hpBar.fullWidth * ratio);
      }
      // 护盾视觉：shieldTimer 每帧续期（装饰计时负责衰减），敌队模型直接置 visible（updateEnemies 不管 shield 模型）。
      tank.shieldTimer = sample.shield ? 0.5 : 0;
      tank.model.shield.visible = sample.shield;
      // 增益标记位（协议 BUFF_FLAG_*）：滚动续期，驱动远端开火的速射/超载视觉复现（fireTank 读这两个计时）。
      const buffs = this.pvpRemoteBuffs.get(playerId) ?? 0;
      tank.rapidTimer = (buffs & BUFF_FLAG_RAPID) !== 0 ? 0.5 : 0;
      tank.overdriveTimer = (buffs & BUFF_FLAG_OVERDRIVE) !== 0 ? 0.5 : 0;
      // 无人机激活标记：远程队友的模型可见性/环绕由 updatePlayers 按 droneTimer 驱动；
      // 敌方远程坦克不在 updatePlayers 覆盖范围（PvE 敌人没有无人机），这里直接驱动。
      const hasDrone = (buffs & BUFF_FLAG_DRONE) !== 0;
      tank.droneTimer = hasDrone ? 0.5 : 0;
      if (tank.team === 'enemy') {
        tank.model.drone.visible = tank.alive && hasDrone;
        if (tank.model.drone.visible) {
          const orbit = this.elapsed * 2.4 + tank.playerIndex * Math.PI;
          tank.model.drone.position.set(Math.cos(orbit) * 1.25, 2.05 + Math.sin(orbit * 1.7) * 0.12, Math.sin(orbit) * 1.25);
          tank.model.drone.rotation.y = -orbit;
        }
      }
      tank.prevX = tank.curX = sample.x;
      tank.prevZ = tank.curZ = sample.z;
      tank.prevBody = sample.body;
      tank.prevTurret = sample.turret;
    }
  }

  /** PvP 迷雾节拍：每 6 个固定步（≈10Hz）重算可见集，viewers = 本方存活坦克（自己 + 队友，共享视野），朝向取炮塔角（扇形视野中轴）。 */
  private updatePvpFogStep(): void {
    if (!this.pvpContext || !this.fog) return;
    this.pvpFogStep += 1;
    if (this.pvpFogStep < 6) return;
    this.pvpFogStep = 0;
    const viewers = this.players
      .filter((player) => player.alive)
      .map((player) => ({ x: player.model.root.position.x, z: player.model.root.position.z, angle: player.turretAngle }));
    // 本方全队阵亡时全图揭开；revealAll 不是持久状态——有人重生后下一拍 update 会重算，迷雾自动恢复。
    if (viewers.length === 0) this.fog.revealAll();
    else this.fog.update(viewers);
  }

  /** PvP 迷雾实体过滤（每渲染帧，须在 updateAmbientAnimations 之后）：不可见的敌队坦克与敌方炮弹隐藏。 */
  private applyFogVisibility(): void {
    const fog = this.fog;
    if (!fog) return;
    for (const enemy of this.enemies) {
      enemy.model.root.visible = enemy.alive && fog.isVisible(enemy.model.root.position.x, enemy.model.root.position.z);
    }
    for (const projectile of this.projectiles) {
      if (projectile.team === 'enemy') {
        projectile.root.visible = fog.isVisible(projectile.root.position.x, projectile.root.position.z);
      }
    }
    // 迷雾一致性（2026-07-26）：敌方地雷/拾取物按「已探索格」显隐，与雷达同口径——
    // 此前 3D 场景不过滤，对手在迷雾深处布的雷直接可见，伏击道具形同虚设。
    // 本方（含队友）的雷恒可见；PvE 无迷雾不受影响。
    for (const mine of this.mines) {
      mine.root.visible = mine.ownerTeam === 'player' || fog.isExplored(mine.x, mine.z);
    }
    for (const pickup of this.pickups) {
      pickup.root.visible = fog.isExplored(pickup.x, pickup.z);
    }
  }

  // -------------------------------------------------------------------------
  // PvP 网络事件处理（由 OnlinePvP 协调器订阅 NetClient 后转发）
  // -------------------------------------------------------------------------

  /** 收到远程玩家快照：写入插值缓冲。 */
  handlePvpRelay(from: string, snapshot: TankSnapshot): void {
    if (!this.pvpContext) return;
    this.remoteBuffer.upsert(from, snapshot);
    // RemoteBuffer 不透传可选 buffs 字段，最新增益标记在这里单独缓存（syncRemoteTanks 消费）。
    this.pvpRemoteBuffs.set(from, snapshot.buffs ?? 0);
  }

  /** 收到远程战斗事件（fire/hit/died/mine）。 */
  handlePvpEvent(from: string, event: CombatEvent): void {
    if (!this.pvpContext) return;
    switch (event.kind) {
      case 'fire': {
        // 按事件参数复现开火（炮弹为视觉副本，命中由射手端检测上报）。
        // 不再借道 fireTank：那会用滞后 ~110ms 的插值位姿/炮塔角出弹，快速甩炮时弹道方向明显错位。
        this.spawnRemoteFire(event, this.tankByPlayerId(from));
        break;
      }
      case 'mine': {
        const tank = this.tankByPlayerId(from);
        this.spawnMine(event.x, event.z, tank?.id ?? 0);
        break;
      }
      case 'drone': {
        // 远端无人机的视觉弹道副本（命中由射手端检测上报 hit，与 fire 复现同口径）。
        this.spawnRemoteDroneShot(event.x, event.z, event.angle, this.tankByPlayerId(from));
        break;
      }
      case 'hit': {
        // 受害者权威血量：命中自己的坦克才在本地扣血（复用护盾/无敌帧逻辑）。
        if (event.targetId !== this.pvpContext.selfId) break;
        // 互信模型下的最小自保：伤害必须是有限正数并钳制（常规 1 / 超载 2，上限 4 给未来留量），
        // 防止异常端用 NaN/Infinity 直接打穿本地血量。
        const damage = Number.isFinite(event.damage) ? Math.min(Math.max(event.damage, 0), 4) : 0;
        if (damage <= 0) break;
        const self = this.selfTank();
        if (self) {
          this.pvpLastAttacker = from;
          this.damageTank(self, damage);
          this.pvpLastAttacker = null;
        }
        break;
      }
      case 'died': {
        // 击杀播报是公共信息，不受迷雾影响；同时更新本地比分缓存。
        const victimName = this.pvpNames.get(from) ?? '对手';
        const killer = event.killerId
          ? this.pvpContext.members.find((member) => member.playerId === event.killerId)
          : null;
        if (killer) {
          this.pvpScore[killer.team === 'alpha' ? 0 : 1] += 1;
          const killerName = this.pvpNames.get(killer.playerId) ?? '对手';
          if (killer.playerId === this.pvpContext.selfId) {
            this.ui.toast('✓', '击杀确认', `你击毁了 ${victimName}`);
          } else {
            this.ui.toast('×', '击杀播报', `${killerName} 击毁了 ${victimName}`);
          }
        } else {
          this.ui.toast('×', '击杀播报', `${victimName} 自毁了`);
        }
        // First Blood / 连杀阶梯记账（远端死亡路径；自己阵亡走 destroyTank）。
        this.registerPvpKill(event.killerId, from);
        // 死亡者重生位置会瞬移，清掉旧快照避免从死亡点拖影回出生点。
        this.remoteBuffer.remove(from);
        const tank = this.tankByPlayerId(from);
        if (tank && tank.alive) {
          tank.alive = false;
          tank.model.root.visible = false;
          TEMP_VECTOR_C.copy(tank.model.root.position).y = 0.45;
          this.vfx.explosion(TEMP_VECTOR_C, tank.team === 'player' ? 0x66dfff : 0xff7358, 1);
          this.audio.play('explode', 0, { x: tank.model.root.position.x, z: tank.model.root.position.z });
        }
        break;
      }
    }
  }

  /** 收到 match_end：停模拟、沿用胜利/失败慢动作演出，~800ms 后出结算屏。 */
  handlePvpMatchEnd(result: MatchResult): void {
    if (!this.pvpContext || this.pvpResult) return;
    this.pvpResult = result;
    this.pvpScore = [result.score[0], result.score[1]];
    // 对局结束时若退出遮罩还开着，先关掉（避免盖住结算屏）
    if (this.pvpPauseOverlay) {
      this.pvpPauseOverlay = false;
      this.ui.hidePause();
    }
    const context = this.pvpContext;
    const victory = result.winnerTeam !== null && result.winnerTeam === context.selfTeam;
    // 平局按 defeat 侧演出处理，结算屏会显示平局徽章。
    this.resultCommitted = true;
    this.phase = victory ? 'victory' : 'defeat';
    this.input.clear();
    while (this.projectiles.length > 0) this.removeProjectile(this.projectiles.length - 1);
    if (victory) {
      this.audio.play('win');
      this.vfx.explosion(TEMP_VECTOR_C.set(0, 2.4, -2), 0xffd950, 1.2);
    } else {
      this.audio.play('lose');
      this.shake = 0.8;
    }
    this.pvpUi().setMatchHud?.(null);
    // 胜负宣布横幅（UIController.showBanner 为既有能力，直接调用）。
    if (result.winnerTeam === null) {
      this.ui.showBanner('DRAW', '势均力敌');
    } else {
      this.ui.showBanner(victory ? 'VICTORY' : 'DEFEAT', victory ? '胜利！' : '惜败…');
      // 胜利演出：胜方坦克处烟花/彩带/光环/聚光柱，败方坦克处暗色闷烟。
      const loserTeam: TeamId = result.winnerTeam === 'alpha' ? 'bravo' : 'alpha';
      this.ensureVictoryFx().celebrate(this.pvpTeamPositions(context, result.winnerTeam), this.pvpTeamPositions(context, loserTeam));
    }
    window.setTimeout(() => {
      this.pvpUi().showMatchResult?.(result, context.selfId, context.selfTeam);
    }, 800);
  }

  /** 远程玩家被移出房间（对局中主动离开或断线即移出，无宽限）：清缓冲、爆炸演出后下场其坦克。 */
  handlePvpPlayerLeft(playerId: string): void {
    if (!this.pvpContext) return;
    this.remoteBuffer.remove(playerId);
    this.pvpRemoteBuffs.delete(playerId);
    this.pvpNames.delete(playerId);
    const tank = this.tankByPlayerId(playerId);
    if (tank) {
      tank.alive = false;
      // 明确视觉反馈：复用阵亡爆炸效果，避免坦克凭空消失。
      if (tank.model.root.visible) {
        TEMP_VECTOR_C.copy(tank.model.root.position).y = 0.45;
        this.vfx.explosion(TEMP_VECTOR_C, tank.team === 'player' ? 0x66dfff : 0xff7358, 1);
        this.audio.play('explode', 0, { x: tank.model.root.position.x, z: tank.model.root.position.z });
      }
      tank.model.root.visible = false;
    }
  }

  pauseGame(): void {
    if (this.phase !== 'playing') return;
    // PvP 对局不暂停本地模拟（服务端计时）：暂停屏仅作「退出对战」入口，ESC/暂停键开合遮罩
    if (this.pvpContext) {
      if (this.pvpPauseOverlay) {
        this.pvpPauseOverlay = false;
        this.ui.hidePause();
        return;
      }
      this.pvpPauseOverlay = true;
      this.input.clear();
      this.ui.showPause(true);
      this.audio.play('ui');
      return;
    }
    this.phase = 'paused';
    this.input.clear();
    this.ui.showPause();
    this.audio.play('ui');
  }

  resumeGame(): void {
    // PvP 遮罩不走 paused 阶段，单独关闭
    if (this.pvpPauseOverlay) {
      this.pvpPauseOverlay = false;
      this.ui.hidePause();
      this.audio.play('ui');
      return;
    }
    if (this.phase !== 'paused') return;
    this.phase = 'playing';
    this.clock.getDelta();
    this.input.clear();
    this.ui.hidePause();
    this.audio.play('ui');
  }

  restartGame(): void {
    // PvP 对局不支持本地重开（再来一局走房间 ready 流程）。
    if (this.pvpContext) return;
    void this.startGame({ ...this.selection });
  }

  quitToMenu(): void {
    this.phase = 'menu';
    this.resultCommitted = false;
    this.resetPvpState();
    this.input.clear();
    this.clearWorld();
    this.buildMenuScene();
    this.ui.showMenu();
    this.audio.play('ui');
  }

  nextLevel(): void {
    const next = this.selection.levelIndex >= LEVELS.length - 1 ? 0 : this.selection.levelIndex + 1;
    this.ui.setSelection({ levelIndex: next });
    void this.startGame({ ...this.selection, levelIndex: next });
  }

  forceVictory(): void {
    // PvP 胜负由服务端 match_end 判定，调试强制结束不适用于联机对局。
    if (this.pvpContext) return;
    if (this.phase === 'playing') this.finish(true);
  }

  forceDefeat(): void {
    if (this.pvpContext) return;
    if (this.phase === 'playing') this.finish(false);
  }

  getSnapshot(): GameSnapshot {
    const info = this.renderer.info;
    const boss = this.enemies.find((enemy) => enemy.kind === 'boss' && enemy.alive);
    return {
      phase: this.phase,
      mode: this.selection.mode,
      levelIndex: this.selection.levelIndex,
      difficulty: this.selection.difficulty,
      score: this.score,
      combo: this.combo,
      maxCombo: this.maxCombo,
      coreHealth: this.core?.health ?? 0,
      coreMaxHealth: this.core?.maxHealth ?? 0,
      defeated: this.defeated,
      totalEnemies: totalEnemies(this.level),
      waveIndex: Math.max(0, this.wave.index),
      totalWaves: this.level.waves.length,
      players: this.players.map((player) => ({
        health: Math.max(0, player.health),
        maxHealth: player.maxHealth,
        lives: Math.max(0, player.lives),
        status: !player.alive
          ? (player.lives > 0 ? 'RESPAWN' : 'DOWN')
          : player.droneTimer > 0
            ? 'DRONE'
            : player.overdriveTimer > 0
              ? 'BOOST'
              : player.shieldTimer > 0
                ? 'SHIELD'
                : player.rapidTimer > 0
                  ? 'RAPID'
                  : player.fireCooldown <= 0
                    ? 'READY'
                    : 'RELOAD',
        powerLabel: player.droneTimer > 0
          ? '辅助无人机'
          : player.overdriveTimer > 0
            ? '涡轮超载'
            : player.shieldTimer > 0
              ? '泡泡护盾'
              : player.rapidTimer > 0
                ? '汽水速射'
                : '反弹炮弹',
        alive: player.alive,
      })),
      enemies: this.enemies.filter((enemy) => enemy.alive).length,
      projectiles: this.projectiles.length,
      pickups: this.pickups.length,
      pickupSpots: this.pickupSpots.length,
      hazards: this.hazards.length,
      mines: this.mines.length,
      bossHealth: boss?.health ?? null,
      bossMaxHealth: boss?.maxHealth ?? null,
      elapsed: this.elapsed,
      pvp: this.pvpContext ? this.pvpHudState() : null,
      fps: this.fps,
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
    };
  }

  private bindUI(): void {
    this.ui.setActions({
      onStart: (selection) => void this.startGame(selection),
      onPause: () => this.pauseGame(),
      onResume: () => this.resumeGame(),
      onRestart: () => this.restartGame(),
      onQuit: () => {
        // 联机对局退出须经 OnlinePvP（leave_room 触发 forfeit 判定）；
        // 本地直接 quitToMenu 会漏发离场消息，变成心跳超时判负。
        if (this.pvpContext && this.pvpQuitHandler) {
          this.pvpQuitHandler();
          return;
        }
        this.quitToMenu();
      },
      onNext: () => this.nextLevel(),
      onSoundToggle: () => {
        this.requestAudioUnlock();
        const enabled = this.audio.toggle();
        this.audio.play('ui');
        return enabled;
      },
      onTouchMove: (x, y) => this.input.setTouchMove(x, y),
      onTouchAim: (x, y) => this.input.setTouchAim(x, y),
      onTouchFire: (active) => this.input.setTouchFire(active),
    });
    this.ui.setSoundEnabled(this.audio.enabled);
  }

  private bindLifecycle(): void {
    window.addEventListener('resize', this.resize);
    window.addEventListener('orientationchange', this.resize);
    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.contextLost = true;
      this.phase = 'paused';
      this.ui.showPause();
      this.ui.toast('!', '图形上下文暂停', '浏览器正在恢复 3D 战场');
    });
    this.canvas.addEventListener('webglcontextrestored', () => {
      this.contextLost = false;
      this.renderer.resetState();
      this.ui.toast('✓', '战场恢复', '可以继续作战');
    });
  }

  private createLighting(): void {
    this.hemisphere = new THREE.HemisphereLight(0xbceeff, 0x35506b, 2.1);
    this.scene.add(this.hemisphere);
    this.keyLight = new THREE.DirectionalLight(0xfff0c4, 3.2);
    this.keyLight.position.set(-11, 20, 9);
    this.keyLight.castShadow = this.shadowsEnabled;
    const shadowSize = this.mobileLike ? 512 : 768;
    this.keyLight.shadow.mapSize.set(shadowSize, shadowSize);
    this.keyLight.shadow.camera.left = -24;
    this.keyLight.shadow.camera.right = 24;
    this.keyLight.shadow.camera.top = 22;
    this.keyLight.shadow.camera.bottom = -22;
    this.keyLight.shadow.camera.near = 1;
    this.keyLight.shadow.camera.far = 60;
    this.keyLight.shadow.bias = -0.0008;
    this.scene.add(this.keyLight);
    this.fillLight = new THREE.DirectionalLight(0x74dfff, 1.1);
    this.fillLight.position.set(14, 10, -10);
    this.scene.add(this.fillLight);
    this.rimLight = new THREE.DirectionalLight(0xff7abf, 0.8);
    this.rimLight.position.set(-9, 8, -14);
    this.scene.add(this.rimLight);
    this.coreLight = new THREE.PointLight(0xffdf59, 2.2, 12, 2);
    this.coreLight.position.set(0, 3, 7);
    this.scene.add(this.coreLight);
  }

  private buildMenuScene(): void {
    this.level = LEVELS[this.ui.getSelection().levelIndex];
    this.setupEnvironment(this.level);
    this.menuTanks = [];
    const p1 = this.models.createTank(this.playerPalette(0), 'player');
    p1.root.position.set(-2.1, 0, 0.7);
    p1.root.rotation.y = -0.35;
    p1.root.scale.setScalar(1.25);
    this.gameplayRoot.add(p1.root);
    this.menuTanks.push(p1);
    const enemy = this.models.createTank(this.enemyPalette('bruiser'), 'bruiser');
    enemy.root.position.set(2.2, 0, -0.4);
    enemy.root.rotation.y = 2.65;
    enemy.root.scale.setScalar(1.12);
    this.gameplayRoot.add(enemy.root);
    this.menuTanks.push(enemy);
    const coreModel = this.models.createCore(this.level.palette.accent);
    coreModel.root.position.set(0, 0, 3.9);
    this.gameplayRoot.add(coreModel.root);
    this.core = {
      ...coreModel,
      x: 0,
      z: 3.9,
      health: 6,
      maxHealth: 6,
      invulnerable: 0,
      pulse: 0,
    };
    this.camera.position.set(0, 13.6, 17.5);
    this.cameraTarget.set(0, 0.8, 1.2);
    this.camera.lookAt(this.cameraTarget);
    this.scene.background = new THREE.Color(this.level.palette.sky);
    this.scene.fog = new THREE.Fog(this.level.palette.fog, 24, 72);
  }

  private buildLevel(): void {
    this.setupEnvironment(this.level);
    if (this.selection.mode === 'pvp') {
      // PvP：无核心、无波次、无出怪门；按房间成员生成双方坦克并建战争迷雾。
      this.spawnPvpTanks();
      this.buildObstacles();
      this.buildHazards();
      this.buildPickupSpots();
      this.buildPvpFog();
      this.wave = { index: -1, queue: [], spawnTimer: 0, transitionTimer: 0, completionTimer: 0 };
      this.pickupSpawnTimer = 2.6;
      this.cameraTarget.set(0, 0, 0);
      const cameraHeight = this.mobileLike ? 28 : 23;
      this.camera.position.set(0, cameraHeight, this.mobileLike ? 21 : 18);
      this.camera.lookAt(this.cameraTarget);
      return;
    }
    this.core = this.createCoreEntity();
    this.spawnPlayers();
    this.buildObstacles();
    this.buildHazards();
    this.buildPickupSpots();
    this.buildSpawnGates();
    this.wave = { index: -1, queue: [], spawnTimer: 0, transitionTimer: 1.65, completionTimer: 0 };
    this.pickupSpawnTimer = 2.6;
    this.cameraTarget.set(0, 0, this.level.corePosition.z * 0.1);
    const cameraHeight = this.mobileLike ? 28 : 23;
    this.camera.position.set(0, cameraHeight, this.mobileLike ? 21 : 18);
    this.camera.lookAt(this.cameraTarget);
  }

  private setupEnvironment(level: LevelConfig): void {
    this.scene.background = new THREE.Color(level.palette.sky);
    this.scene.fog = new THREE.Fog(level.palette.fog, level.scenery === 'moon' ? 26 : 30, level.scenery === 'moon' ? 68 : 82);
    this.hemisphere.color.set(level.scenery === 'moon' ? 0x8f8cff : 0xc8f3ff);
    this.hemisphere.groundColor.set(level.scenery === 'desert' ? 0x8a4f3a : level.scenery === 'moon' ? 0x241b45 : 0x477a5a);
    this.keyLight.color.set(level.scenery === 'moon' ? 0xbfd0ff : 0xfff2c9);
    this.fillLight.color.set(level.palette.accent);
    this.rimLight.color.set(level.scenery === 'desert' ? 0xff7867 : 0xff75c8);
    this.renderer.toneMappingExposure = level.scenery === 'moon' ? 1.18 : 1.06;

    const texture = this.materials.createGroundTexture(level.palette, level.scenery);
    const groundMaterial = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.93, metalness: level.scenery === 'moon' ? 0.08 : 0 });
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(level.arena.width + 1.6, level.arena.depth + 1.6), groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.02;
    ground.receiveShadow = true;
    this.decorRoot.add(ground);

    const outerGround = new THREE.Mesh(
      new THREE.PlaneGeometry(level.arena.width + 30, level.arena.depth + 30),
      this.materials.toon(level.scenery === 'desert' ? 0xb96f4f : level.scenery === 'moon' ? 0x292740 : 0x6ea260),
    );
    outerGround.rotation.x = -Math.PI / 2;
    outerGround.position.y = -0.12;
    this.decorRoot.add(outerGround);
    this.decorRoot.add(this.models.createArenaBorder(level));
    this.decorRoot.add(this.models.createScenery(level));
    this.addArenaMarkings(level);
    // 场景氛围：天空穹顶/尘埃/场外装饰按关卡调色板重配（apply 可重入）；
    // 灯光打磨与阴影视锥贴合由 stylizeLights 统一负责（替代此前在此手写的阴影相机派生，避免两处重复拟合同一视锥）。
    this.styler.apply(level);
    this.styler.stylizeLights(
      { hemisphere: this.hemisphere, keyLight: this.keyLight, fillLight: this.fillLight, rimLight: this.rimLight },
      level,
    );
  }

  private addArenaMarkings(level: LevelConfig): void {
    const markMaterial = new THREE.MeshBasicMaterial({ color: level.palette.rim, transparent: true, opacity: 0.27, side: THREE.DoubleSide, depthWrite: false, toneMapped: false });
    const center = new THREE.Mesh(new THREE.RingGeometry(2.2, 2.35, 32), markMaterial);
    center.rotation.x = -Math.PI / 2;
    center.position.y = 0.015;
    this.decorRoot.add(center);
    for (let i = -3; i <= 3; i += 1) {
      const dash = new THREE.Mesh(new THREE.PlaneGeometry(0.28, 1.3), markMaterial);
      dash.rotation.x = -Math.PI / 2;
      dash.position.set(0, 0.018, i * 2.7);
      this.decorRoot.add(dash);
    }
    const coreRing = new THREE.Mesh(new THREE.RingGeometry(2.15, 2.3, 24), new THREE.MeshBasicMaterial({ color: level.palette.accent, transparent: true, opacity: 0.36, side: THREE.DoubleSide, depthWrite: false }));
    coreRing.rotation.x = -Math.PI / 2;
    coreRing.position.set(level.corePosition.x, 0.02, level.corePosition.z);
    this.decorRoot.add(coreRing);
  }

  private createCoreEntity(): CoreEntity {
    const model = this.models.createCore(this.level.palette.accent);
    model.root.position.set(this.level.corePosition.x, 0, this.level.corePosition.z);
    this.gameplayRoot.add(model.root);
    const maxHealth = this.selection.mode === 'coop' ? 8 : 6;
    this.coreLight.position.set(this.level.corePosition.x, 3.2, this.level.corePosition.z);
    return {
      ...model,
      x: this.level.corePosition.x,
      z: this.level.corePosition.z,
      health: maxHealth,
      maxHealth,
      invulnerable: 0,
      pulse: 0,
    };
  }

  private spawnPlayers(): void {
    const count = this.selection.mode === 'coop' ? 2 : 1;
    for (let index = 0; index < count; index += 1) {
      const spawn = this.level.playerSpawns[index];
      const model = this.models.createTank(this.playerPalette(index), 'player');
      model.root.position.set(spawn.x, 0, spawn.z);
      model.root.rotation.y = Math.PI;
      this.dynamicRoot.add(model.root);
      const player: TankEntity = {
        id: ++this.entitySerial,
        team: 'player',
        kind: 'player',
        playerIndex: index,
        model,
        velocity: new THREE.Vector3(),
        bodyAngle: Math.PI,
        turretAngle: Math.PI,
        health: 4,
        maxHealth: 4,
        lives: 3,
        radius: 1.15,
        speed: index === 0 ? 7.4 : 7.1,
        fireCooldown: 0.45,
        fireInterval: 0.56,
        projectileSpeed: 18,
        damage: 1,
        alive: true,
        respawnTimer: 0,
        invulnerable: 1.7,
        shieldTimer: 0,
        rapidTimer: 0,
        overdriveTimer: 0,
        droneTimer: 0,
        droneCooldown: 0,
        hazardTimer: 0,
        inputIntensity: 0,
        speedRatio: 0,
        handlingLean: 0,
        handlingPitch: 0,
        recoil: 0,
        dustTimer: 0,
        hitFlash: 0,
        spawnPoint: { ...spawn },
        aiBlocked: 0,
        aiTurnDirection: 1,
        aiRetarget: 0,
        targetId: null,
        bossSummonMask: 0,
        bossBurstTimer: 0,
        spawnAngle: Math.PI,
        // 出生即 prev=cur，防止插值从旧位置拉面条。
        prevX: spawn.x,
        prevZ: spawn.z,
        prevBody: Math.PI,
        prevTurret: Math.PI,
        curX: spawn.x,
        curZ: spawn.z,
      };
      this.players.push(player);
      TEMP_VECTOR_C.copy(model.root.position).y = 0.2;
      this.vfx.spawn(TEMP_VECTOR_C, index === 0 ? 0xffd44d : 0x5be5ff);
    }
  }

  /**
   * PvP 按房间成员生成双方坦克（设计文档 §7 相对队伍映射）：
   * 本方（自己 playerIndex=0 本地输入 + 队友 playerIndex=2 远程）→ team:'player'；
   * 对方 → team:'enemy'、kind:'player'、remote 标记。出生点按绝对队伍 + 队内 slot 取镜像点。
   */
  private spawnPvpTanks(): void {
    const context = this.pvpContext;
    if (!context) return;
    for (const member of context.members) {
      const isAlly = member.team === context.selfTeam;
      const isSelf = member.playerId === context.selfId;
      // 出生点按【绝对队伍】选择（alpha 恒左 / bravo 恒右）。
      // 曾按相对 ally/enemy 选择：两端都把自己当 ally，双方出生在同一点堆叠。
      const spawns = member.team === 'alpha' ? this.level.playerSpawns : this.level.enemySpawns;
      const spawn = spawns[member.slot % spawns.length];
      const palette = isSelf
        ? this.playerPalette(0)
        : isAlly
          ? this.playerPalette(1)
          : this.enemyPalette('scout');
      const model = this.models.createTank(palette, 'player');
      // 出生面向场地中央（对手方向）。
      const face = angleTo(spawn.x, spawn.z, 0, 0);
      model.root.position.set(spawn.x, 0, spawn.z);
      model.root.rotation.y = -face;
      this.dynamicRoot.add(model.root);
      const tank: TankEntity = {
        id: ++this.entitySerial,
        team: isAlly ? 'player' : 'enemy',
        kind: 'player',
        playerIndex: isSelf ? 0 : isAlly ? 2 : -1,
        model,
        velocity: new THREE.Vector3(),
        bodyAngle: face,
        turretAngle: face,
        // pvp 血量 3：3 血 = 3 发击杀 ≈1.7s TTK，配合 180s/5 杀与 3s 重生的节奏调校（PvE 玩家 4 不变）。
        health: 3,
        maxHealth: 3,
        // pvp 无限重生（MATCH_RULES.respawnSec），lives 恒 1 仅供 HUD 占位。
        lives: 1,
        radius: 1.15,
        speed: 7.4,
        fireCooldown: 0.45,
        fireInterval: 0.56,
        projectileSpeed: 18,
        damage: 1,
        alive: true,
        respawnTimer: 0,
        // 远程坦克的无敌帧由其客户端自行判定，本地不拦截命中上报。
        invulnerable: isSelf ? 2.4 : 0,
        shieldTimer: 0,
        rapidTimer: 0,
        overdriveTimer: 0,
        droneTimer: 0,
        droneCooldown: 0,
        hazardTimer: 0,
        inputIntensity: 0,
        speedRatio: 0,
        handlingLean: 0,
        handlingPitch: 0,
        recoil: 0,
        dustTimer: 0,
        hitFlash: 0,
        spawnPoint: { ...spawn },
        aiBlocked: 0,
        aiTurnDirection: 1,
        aiRetarget: 0,
        targetId: null,
        bossSummonMask: 0,
        bossBurstTimer: 0,
        spawnAngle: face,
        remote: !isSelf,
        remotePlayerId: member.playerId,
        // 出生即 prev=cur（远程坦克不参与本地插值，仅为字段完整）。
        prevX: spawn.x,
        prevZ: spawn.z,
        prevBody: face,
        prevTurret: face,
        curX: spawn.x,
        curZ: spawn.z,
      };
      (isAlly ? this.players : this.enemies).push(tank);
      // 头顶名牌：挂在 root 下，随坦克显隐（迷雾/死亡）与重生位置自然联动。
      model.root.add(this.createPvpNameTag(member.name, isAlly, isSelf));
      // 头顶血条：远程坦克专属（自己看 HUD），随 root 显隐同样受迷雾控制。
      if (!isSelf) {
        const hpBar = this.createPvpHpBar(isAlly);
        model.root.add(hpBar.root);
        tank.hpBar = hpBar;
      }
      TEMP_VECTOR_C.copy(model.root.position).y = 0.2;
      this.vfx.spawn(TEMP_VECTOR_C, isAlly ? (isSelf ? 0xffd44d : 0x5be5ff) : 0xff5b7a);
    }
  }

  /**
   * PvP 击杀事件记账（First Blood / 连杀阶梯 / 连杀终结播报；纯客户端表现层，
   * 不影响比分与结算）。数据源两路互补不重叠：远端死亡走 handlePvpEvent 'died'，
   * 自己阵亡走 destroyTank 本端分支。
   */
  private registerPvpKill(killerId: string | null, victimId: string): void {
    if (!this.pvpContext) return;
    const victimStreak = this.pvpKillStreaks.get(victimId) ?? 0;
    this.pvpKillStreaks.set(victimId, 0);
    if (!killerId) return; // 自毁/环境击杀：只清受害者连杀
    const killerName = killerId === this.pvpContext.selfId ? '你' : this.pvpNames.get(killerId) ?? '对手';
    const victimName = victimId === this.pvpContext.selfId ? '你' : this.pvpNames.get(victimId) ?? '对手';
    if (victimStreak >= 3) {
      this.ui.toast('⚔', '连杀终结', `${killerName}终结了${victimName}的 ${victimStreak} 连杀`);
    }
    const streak = (this.pvpKillStreaks.get(killerId) ?? 0) + 1;
    this.pvpKillStreaks.set(killerId, streak);
    if (!this.pvpFirstBloodDone) {
      this.pvpFirstBloodDone = true;
      this.ui.toast('⚔', 'FIRST BLOOD', `${killerName}拿下本局首杀！`);
      this.audio.play('wave');
      return; // 首杀播报优先，streak=1 无阶梯播报
    }
    if (streak === 2) this.ui.toast('⚡', '双杀', `${killerName}连续击毁 2 辆坦克`);
    else if (streak === 3) this.ui.toast('⚡', '三连杀', `${killerName}火力全开！`);
    else if (streak >= 5 && streak % 2 === 1) this.ui.toast('♛', '势不可挡', `${killerName}已 ${streak} 连杀！`);
  }

  /**
   * PvP 头顶血条（仅远程坦克创建；本端玩家用 HUD 血条）。挂在坦克 root 下，
   * 随迷雾/死亡显隐自然联动；fill 左锚定，scale.x 按血量比例伸缩。
   * 材质不标 persistent，随坦克 root 由 clearWorld 统一释放。
   */
  private createPvpHpBar(isAlly: boolean): { root: any; fill: any; fullWidth: number } {
    const root = new THREE.Group();
    const fullWidth = 1.5;
    const bg = new THREE.Sprite(new THREE.SpriteMaterial({ color: 0x17283d, transparent: true, opacity: 0.7, depthWrite: false }));
    bg.scale.set(fullWidth + 0.1, 0.2, 1);
    root.add(bg);
    const fill = new THREE.Sprite(new THREE.SpriteMaterial({ color: isAlly ? 0x4fd8ff : 0xff7a3d, transparent: true, opacity: 0.95, depthWrite: false }));
    // 左锚定：center.x=0 时 scale.x 从左端向右生长，血条缩短方向固定。
    fill.center.set(0, 0.5);
    fill.position.x = -fullWidth / 2;
    fill.scale.set(fullWidth, 0.13, 1);
    root.add(fill);
    // 名牌底边 y=3.1，血条居其下方、护盾视觉之上。
    root.position.y = 2.74;
    return { root, fill, fullWidth };
  }

  /**
   * PvP 头顶名牌（仅 spawnPvpTanks 调用，PvE 不创建）：Canvas 一次性绘制，
   * 运行期不重绘。相对队伍配色——本方友军青（对齐 UI alpha 色）、对方敌军橙红
   * （对齐 UI bravo 色），自己文字金色高亮并前置「我·」。canvas/texture/material
   * 均不标 persistent，clearWorld 的 disposeBranch 会随坦克 root 一并释放。
   */
  private createPvpNameTag(name: string, isAlly: boolean, isSelf: boolean): any {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const context = canvas.getContext('2d')!;
    context.clearRect(0, 0, 256, 64);
    // 圆角底牌 + 队色描边（描边色 = styles.css 的 --cyan / --orange）。
    const teamColor = isAlly ? '#4fd8ff' : '#ff7a3d';
    const plate = (x: number, y: number, width: number, height: number, radius: number): void => {
      context.beginPath();
      context.moveTo(x + radius, y);
      context.lineTo(x + width - radius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + radius);
      context.lineTo(x + width, y + height - radius);
      context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      context.lineTo(x + radius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - radius);
      context.lineTo(x, y + radius);
      context.quadraticCurveTo(x, y, x + radius, y);
      context.closePath();
    };
    context.fillStyle = 'rgba(23,40,61,0.85)';
    plate(4, 6, 248, 52, 16);
    context.fill();
    context.strokeStyle = teamColor;
    context.lineWidth = 4;
    plate(4, 6, 248, 52, 16);
    context.stroke();
    // 昵称：粗体圆润黑体（与 UI 的 Arial Rounded 风格一致），超长自动缩字号。
    const label = isSelf ? `我·${name}` : name;
    let fontSize = 30;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    do {
      context.font = `900 ${fontSize}px "Arial Rounded MT Bold", "PingFang SC", "Microsoft YaHei", sans-serif`;
      fontSize -= 2;
    } while (context.measureText(label).width > 216 && fontSize >= 16);
    context.fillStyle = isSelf ? '#ffd34f' : '#fff8df';
    context.fillText(label, 128, 34);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false }));
    // 约 2.6×0.65 世界单位，锚点底部对齐（y=3.1 处底边起算，高于护盾视觉，不遮挡车体）。
    sprite.scale.set(2.6, 0.65, 1);
    sprite.center.set(0.5, 0);
    sprite.position.y = 3.1;
    return sprite;
  }

  /** PvP 战争迷雾：全障碍烘培挡视线（Crate 摧毁时 unblockArea 开阔视野）。 */
  private buildPvpFog(): void {
    // 视野半径 18（2026-07-26 应要求由默认 9 加倍；扇形半角 65° 与贴身保底圈不变）
    this.fog = new FogOfWar({ width: this.level.arena.width, depth: this.level.arena.depth, sightRadius: 18 });
    this.fog.setObstacles(this.obstacles.map((obstacle) => ({
      x: obstacle.x,
      z: obstacle.z,
      halfW: obstacle.width / 2,
      halfD: obstacle.depth / 2,
      rotation: obstacle.rotation,
      blocksSight: true,
    })));
    this.fog.attachOverlay(this.gameplayRoot);
    // 开局点亮本方半场（出生点一带标为已探索），避免开场全黑的压抑感。
    const context = this.pvpContext;
    if (context) {
      const homeSpawns = context.selfTeam === 'alpha' ? this.level.playerSpawns : this.level.enemySpawns;
      for (const spawn of homeSpawns) this.fog.exploreAt(spawn.x, spawn.z, 14);
    }
  }

  private buildObstacles(): void {
    for (const spec of this.level.obstacles) {
      const model = this.models.createObstacle(spec.kind, spec.width, spec.depth, this.level);
      model.root.position.set(spec.x, 0, spec.z);
      model.root.rotation.y = spec.rotation ?? 0;
      this.gameplayRoot.add(model.root);
      const maxHealth = spec.kind === 'metal' ? Number.POSITIVE_INFINITY : spec.health ?? 3;
      this.obstacles.push({
        id: ++this.entitySerial,
        kind: spec.kind,
        root: model.root,
        x: spec.x,
        z: spec.z,
        width: spec.width,
        depth: spec.depth,
        rotation: spec.rotation ?? 0,
        health: maxHealth,
        maxHealth,
        active: true,
      });
    }
  }

  private buildHazards(): void {
    for (const spec of this.level.hazards) {
      const root = this.models.createHazard(spec.kind, spec.radius, this.level);
      root.position.set(spec.x, 0, spec.z);
      this.gameplayRoot.add(root);
      this.hazards.push({
        id: ++this.entitySerial,
        kind: spec.kind,
        root,
        x: spec.x,
        z: spec.z,
        radius: spec.radius,
        strength: spec.strength ?? (spec.kind === 'mud' ? 0.72 : 1),
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  private buildPickupSpots(): void {
    for (const spec of this.level.pickupSpots) {
      const root = this.models.createPickupPad(this.level);
      root.position.set(spec.x, 0, spec.z);
      this.gameplayRoot.add(root);
      this.pickupSpots.push({
        id: ++this.entitySerial,
        root,
        x: spec.x,
        z: spec.z,
        weight: spec.weight ?? 1,
        kinds: [...spec.kinds],
      });
    }
  }

  private buildSpawnGates(): void {
    for (const spawn of this.level.enemySpawns) {
      const gate = this.models.createSpawnGate(this.level.palette.danger);
      gate.position.set(spawn.x, 0.02, spawn.z);
      this.gameplayRoot.add(gate);
    }
  }

  private loop = (): void => {
    requestAnimationFrame(this.loop);
    if (this.contextLost) return;
    const rawDelta = this.clock.getDelta();
    if (!this.loopEnabled) return;
    const delta = Math.min(rawDelta, MAX_FRAME_DELTA);
    this.updateFps(rawDelta);
    this.handleGlobalInput();

    if (this.phase === 'menu') {
      this.updateMenu(delta);
    } else if (this.phase === 'playing') {
      // Reclaim last frame's dead enemies outside fixed-update iteration.
      this.reclaimDeadEnemies();
      this.advanceFixedSimulation(delta);
      // Send PvP snapshots before render interpolation mutates shared Object3D positions.
      this.sendPvpSnapshotThrottled(delta);
      this.syncRenderTransforms(this.accumulator / FIXED_STEP);
      this.vfx.update(delta);
      this.trails.update(delta);
      this.styler.update(delta);
      this.updateCamera(delta);
      this.updateAmbientAnimations(delta);
      // PvP 每帧驱动（倒计时/远程坦克/迷雾/HUD）须在 updateAmbientAnimations 之后（它会重置 root.visible）。
      this.updatePvpFrame(delta);
      this.updateHud(false);
    } else if (this.phase === 'victory' || this.phase === 'defeat') {
      this.vfx.update(delta * 0.45);
      this.trails.update(delta * 0.45);
      this.styler.update(delta * 0.45);
      // PvP 结算演出随慢动作节奏推进（烟花/彩带/光环/闷烟）。
      this.victoryFx?.update(delta * 0.45);
      this.updateAmbientAnimations(delta * 0.5);
      this.updateCamera(delta * 0.55);
    }

    this.updateDebugPanel();
    this.renderer.render(this.scene, this.camera);
    this.restoreLogicalTransforms();
    this.input.endFrame();
    this.frameCounter += 1;
  };

  private handleGlobalInput(): void {
    if (this.input.consumePressed('Backquote')) this.debugVisible = !this.debugVisible;
    if (this.input.consumePressed('KeyM')) {
      this.requestAudioUnlock();
      this.ui.setSoundEnabled(this.audio.toggle());
    }

    if (this.phase === 'menu') {
      if (this.input.consumePressed('Enter')) void this.startGame(this.ui.getSelection());
      return;
    }

    if (this.phase === 'playing') {
      if (this.input.consumePressed('Escape')) this.pauseGame();
      if (this.input.consumePressed('KeyR')) this.restartGame();
      return;
    }

    if (this.phase === 'paused') {
      if (this.input.consumePressed('Escape')) this.resumeGame();
      return;
    }

    if ((this.phase === 'victory' || this.phase === 'defeat') && this.input.consumePressed('Enter')) {
      if (this.phase === 'victory') this.nextLevel(); else this.restartGame();
    }
  }

  private fixedUpdate(delta: number): void {
    this.elapsed += delta;
    if (this.comboTimer > 0) {
      this.comboTimer -= delta;
      if (this.comboTimer <= 0) this.combo = 1;
    }
    if (this.core) {
      this.core.invulnerable = Math.max(0, this.core.invulnerable - delta);
      this.core.pulse = Math.max(0, this.core.pulse - delta);
    }

    this.updateWave(delta);
    this.updatePlayers(delta);
    this.updateEnemies(delta);
    this.resolveTankSeparation();
    this.updateProjectiles(delta);
    this.updateMines(delta);
    this.updateHazards(delta);
    this.updatePickupSpots(delta);
    this.updatePickups(delta);
    this.checkEndConditions(delta);
    this.updatePvpFogStep();
  }

  /**
   * Advance the fixed-rate simulation and retain the two states required for interpolation.
   * Returning the step count keeps deterministic diagnostics tied to the production path.
   */
  private advanceFixedSimulation(delta: number): number {
    this.accumulator = Math.min(this.accumulator + delta, FIXED_STEP * MAX_CATCH_UP_STEPS);
    let steps = 0;
    while (this.accumulator >= FIXED_STEP && steps < MAX_CATCH_UP_STEPS) {
      // Advance prev only when simulation advances. Sampling on every render frame
      // erases the interpolation interval on high-refresh displays.
      this.capturePrevTransforms();
      this.fixedUpdate(FIXED_STEP);
      this.accumulator -= FIXED_STEP;
      steps += 1;
    }
    if (this.accumulator >= FIXED_STEP) {
      this.accumulator = 0;
      this.droppedSimulationFrames += 1;
    }
    return steps;
  }

  /** Capture logical poses immediately before a fixed step. Remote tanks use RemoteBuffer interpolation. */
  private capturePrevTransforms(): void {
    for (const player of this.players) if (!player.remote) this.captureTankPrev(player);
    for (const enemy of this.enemies) if (!enemy.remote) this.captureTankPrev(enemy);
    for (const projectile of this.projectiles) {
      projectile.prevX = projectile.root.position.x;
      projectile.prevZ = projectile.root.position.z;
    }
    for (const mine of this.mines) mine.prevRotY = mine.root.rotation.y;
    for (const pickup of this.pickups) {
      pickup.prevRotY = pickup.root.rotation.y;
      pickup.prevY = pickup.root.position.y;
    }
  }

  private captureTankPrev(tank: TankEntity): void {
    tank.prevX = tank.model.root.position.x;
    tank.prevZ = tank.model.root.position.z;
    tank.prevBody = tank.bodyAngle;
    tank.prevTurret = tank.turretAngle;
  }

  /**
   * Write lerp(prev, current, alpha) into Three.js objects immediately before rendering.
   * Simulation always reads logical coordinates; restoreLogicalTransforms restores the
   * shared Object3D positions after rendering. Remote PvP tanks are interpolated separately.
   */
  private syncRenderTransforms(alpha: number): void {
    for (const tank of this.activeTanksForSync()) {
      const root = tank.model.root;
      const x = root.position.x;
      const z = root.position.z;
      tank.curX = x;
      tank.curZ = z;
      root.position.x = tank.prevX + (x - tank.prevX) * alpha;
      root.position.z = tank.prevZ + (z - tank.prevZ) * alpha;
      const body = lerpAngle(tank.prevBody, tank.bodyAngle, alpha);
      // Handling feedback owns pitch and roll, so only interpolate yaw here.
      root.rotation.y = -body;
      tank.model.turret.rotation.y = body - lerpAngle(tank.prevTurret, tank.turretAngle, alpha);
    }
    for (const projectile of this.projectiles) {
      const position = projectile.root.position;
      projectile.curX = position.x;
      projectile.curZ = position.z;
      position.x = projectile.prevX + (position.x - projectile.prevX) * alpha;
      position.z = projectile.prevZ + (position.z - projectile.prevZ) * alpha;
    }
    for (const mine of this.mines) {
      mine.curRotY = mine.root.rotation.y;
      mine.root.rotation.y = mine.prevRotY + (mine.curRotY - mine.prevRotY) * alpha;
    }
    for (const pickup of this.pickups) {
      pickup.curRotY = pickup.root.rotation.y;
      pickup.curY = pickup.root.position.y;
      pickup.root.rotation.y = pickup.prevRotY + (pickup.curRotY - pickup.prevRotY) * alpha;
      pickup.root.position.y = pickup.prevY + (pickup.curY - pickup.prevY) * alpha;
    }
    this.renderSyncActive = true;
  }

  /** Restore logical coordinates after rendering for collision, hit tests, and the next fixed step. */
  private restoreLogicalTransforms(): void {
    if (!this.renderSyncActive) return;
    this.renderSyncActive = false;
    for (const tank of this.activeTanksForSync()) {
      tank.model.root.position.x = tank.curX;
      tank.model.root.position.z = tank.curZ;
    }
    for (const projectile of this.projectiles) {
      projectile.root.position.x = projectile.curX;
      projectile.root.position.z = projectile.curZ;
    }
    for (const mine of this.mines) mine.root.rotation.y = mine.curRotY;
    for (const pickup of this.pickups) {
      pickup.root.rotation.y = pickup.curRotY;
      pickup.root.position.y = pickup.curY;
    }
  }

  /** Reuse scratch storage for locally simulated tanks that need render interpolation. */
  private activeTanksForSync(): TankEntity[] {
    const tanks = this.syncTanksScratch;
    tanks.length = 0;
    for (const player of this.players) if (!player.remote) tanks.push(player);
    for (const enemy of this.enemies) if (!enemy.remote) tanks.push(enemy);
    return tanks;
  }

  private updatePlayers(delta: number): void {
    for (const player of this.players) {
      player.fireCooldown -= delta;
      player.invulnerable = Math.max(0, player.invulnerable - delta);
      player.shieldTimer = Math.max(0, player.shieldTimer - delta);
      player.rapidTimer = Math.max(0, player.rapidTimer - delta);
      player.overdriveTimer = Math.max(0, player.overdriveTimer - delta);
      player.droneTimer = Math.max(0, player.droneTimer - delta);
      player.droneCooldown = Math.max(0, player.droneCooldown - delta);
      player.hazardTimer = Math.max(0, player.hazardTimer - delta);
      player.recoil = damp(player.recoil, 0, 8.5, delta);
      player.dustTimer = Math.max(0, player.dustTimer - delta);
      player.hitFlash = Math.max(0, player.hitFlash - delta);
      player.model.shield.visible = player.shieldTimer > 0;
      player.model.drone.visible = player.alive && player.droneTimer > 0;
      if (player.model.drone.visible) {
        const orbit = this.elapsed * 2.4 + player.playerIndex * Math.PI;
        player.model.drone.position.set(Math.cos(orbit) * 1.25, 2.05 + Math.sin(orbit * 1.7) * 0.12, Math.sin(orbit) * 1.25);
        player.model.drone.rotation.y = -orbit;
      }

      // PvP 远程队友：不读输入/不自动瞄准/不开火，位置与生死由快照驱动（本地不重生）；
      // 仅按快照速度推算 inputIntensity 衰减，供履带动画读取。
      if (player.remote) {
        const remoteSpeedRatio = Math.min(1, Math.hypot(player.velocity.x, player.velocity.z) / Math.max(0.001, player.speed));
        player.inputIntensity = damp(player.inputIntensity, remoteSpeedRatio, 12, delta);
        continue;
      }

      if (!player.alive) {
        if (player.lives > 0) {
          player.respawnTimer -= delta;
          if (player.respawnTimer <= 0) this.respawnPlayer(player);
        }
        continue;
      }

      let moveX = 0;
      let moveZ = 0;
      let wantsFire = false;
      // 方向键在非双人模式下同样驾驶 1P：单人/PvP 只有一名本地玩家，
      // 习惯本地 2P（方向键+Enter）的玩家进联机后按方向键否则完全动不了。
      const arrowsDriveP1 = this.selection.mode !== 'coop';
      if (player.playerIndex === 0) {
        moveX = Number(this.input.isDown('KeyD')) - Number(this.input.isDown('KeyA')) + this.input.touchMove.x;
        moveZ = Number(this.input.isDown('KeyS')) - Number(this.input.isDown('KeyW')) + this.input.touchMove.y;
        if (arrowsDriveP1) {
          moveX += Number(this.input.isDown('ArrowRight')) - Number(this.input.isDown('ArrowLeft'));
          moveZ += Number(this.input.isDown('ArrowDown')) - Number(this.input.isDown('ArrowUp'));
        }
        wantsFire =
          this.input.isDown('Space') ||
          this.input.pointerFire ||
          this.input.touchFire ||
          (arrowsDriveP1 && this.input.isDown('Enter', 'Numpad0'));
      } else {
        moveX = Number(this.input.isDown('ArrowRight')) - Number(this.input.isDown('ArrowLeft'));
        moveZ = Number(this.input.isDown('ArrowDown')) - Number(this.input.isDown('ArrowUp'));
        wantsFire = this.input.isDown('Enter', 'Numpad0');
      }
      // PvP 倒计时锁定期：本地输入清零（不移动不开火）。
      if (this.pvpInputLocked()) {
        moveX = 0;
        moveZ = 0;
        wantsFire = false;
      }

      const moveLength = Math.hypot(moveX, moveZ);
      if (moveLength > 1) {
        moveX /= moveLength;
        moveZ /= moveLength;
      }
      const previousSpeed = Math.hypot(player.velocity.x, player.velocity.z);
      const boost = player.overdriveTimer > 0 ? PICKUP_TUNING.overdrive.speedFactor : 1;
      const hazardSpeed = this.hazardSpeedMultiplierAt(player.model.root.position.x, player.model.root.position.z);
      const targetVelocityX = moveX * player.speed * boost * hazardSpeed;
      const targetVelocityZ = moveZ * player.speed * boost * hazardSpeed;
      const movementSmoothing = moveLength > 0.05 ? 13.2 : 18;
      player.velocity.x = damp(player.velocity.x, targetVelocityX, movementSmoothing, delta);
      player.velocity.z = damp(player.velocity.z, targetVelocityZ, movementSmoothing, delta);

      if (player.velocity.lengthSq() > 0.14) {
        const desiredAngle = Math.atan2(player.velocity.x, -player.velocity.z);
        player.bodyAngle = dampAngle(player.bodyAngle, desiredAngle, 13, delta);
      }

      this.moveTank(player, player.velocity.x * delta, player.velocity.z * delta);
      this.updatePlayerAim(player, delta);
      player.model.turret.rotation.y = player.bodyAngle - player.turretAngle;
      this.updatePlayerHandlingFeedback(player, moveX, moveZ, moveLength, previousSpeed, delta);

      if (wantsFire && player.fireCooldown <= 0) this.fireTank(player);
      this.updateDroneSupport(player, delta);
    }
  }

  private updatePlayerAim(player: TankEntity, delta: number): void {
    let targetAngle = player.bodyAngle;
    // 玩家 1 推住瞄准摇杆（超出死区）时：炮管朝摇杆方向瞄准，并刷新 2.5 秒保持窗口。
    if (player.playerIndex === 0 && Math.hypot(this.input.touchAim.x, this.input.touchAim.y) > 0.25) {
      targetAngle = Math.atan2(this.input.touchAim.x, -this.input.touchAim.y);
      this.touchAimHold = 2.5;
    } else if (player.playerIndex === 0 && this.touchAimHold > 0) {
      // 松杆后的保持窗口内：既不动炮管也不自动索敌，维持当前炮管角度。
      this.touchAimHold -= delta;
      return;
    } else {
      const shouldAutoAim = player.playerIndex === 1 || this.mobileLike || !this.input.pointerActive;
      if (shouldAutoAim) {
        const target = this.findNearestEnemy(player.model.root.position.x, player.model.root.position.z, 30);
        if (target) targetAngle = angleTo(player.model.root.position.x, player.model.root.position.z, target.model.root.position.x, target.model.root.position.z);
      } else {
        this.raycaster.setFromCamera(this.input.pointerNdc, this.camera);
        if (this.raycaster.ray.intersectPlane(GROUND_PLANE, this.aimPoint)) {
          targetAngle = angleTo(player.model.root.position.x, player.model.root.position.z, this.aimPoint.x, this.aimPoint.z);
        }
      }
    }
    player.turretAngle = dampAngle(player.turretAngle, targetAngle, 18, delta);
  }

  private updatePlayerHandlingFeedback(player: TankEntity, moveX: number, moveZ: number, moveLength: number, previousSpeed: number, delta: number): void {
    const speed = Math.hypot(player.velocity.x, player.velocity.z);
    const speedRatio = Math.min(1.35, speed / Math.max(0.001, player.speed));
    const rightX = Math.cos(player.bodyAngle);
    const rightZ = Math.sin(player.bodyAngle);
    const forwardX = Math.sin(player.bodyAngle);
    const forwardZ = -Math.cos(player.bodyAngle);
    const lateral = (player.velocity.x * rightX + player.velocity.z * rightZ) / Math.max(0.001, player.speed);
    const forward = (player.velocity.x * forwardX + player.velocity.z * forwardZ) / Math.max(0.001, player.speed);
    const braking = moveLength < 0.08 && previousSpeed > 2.6 ? Math.min(1, previousSpeed / player.speed) : 0;
    const inputTurn = moveLength > 0.08 ? (moveX * rightX + moveZ * rightZ) * 0.08 : 0;

    player.inputIntensity = damp(player.inputIntensity, moveLength, 12, delta);
    player.speedRatio = damp(player.speedRatio, speedRatio, 9, delta);
    player.handlingLean = damp(player.handlingLean, THREE.MathUtils.clamp(-lateral * 0.16 - inputTurn, -0.23, 0.23), 10, delta);
    player.handlingPitch = damp(player.handlingPitch, THREE.MathUtils.clamp(forward * 0.055 + braking * 0.105 - player.recoil * 0.16, -0.18, 0.15), 11, delta);

    player.model.root.rotation.set(player.handlingPitch, -player.bodyAngle, player.handlingLean);
    player.model.root.position.y = Math.sin(this.elapsed * (16 + player.speedRatio * 18) + player.playerIndex) * 0.018 * player.speedRatio + player.recoil * 0.035;

    const trackCycle = this.elapsed * (19 + player.speedRatio * 16);
    const trackLift = 0.022 + player.speedRatio * 0.025;
    player.model.leftTrack.position.y = 0.39 + Math.sin(trackCycle + player.playerIndex) * trackLift * player.speedRatio;
    player.model.rightTrack.position.y = 0.39 + Math.sin(trackCycle + player.playerIndex + Math.PI) * trackLift * player.speedRatio;
    player.model.antennaTip.scale.setScalar(0.09 + Math.sin(this.elapsed * 7 + player.playerIndex) * 0.012 + player.recoil * 0.02);

    if (speedRatio > 0.32 && player.dustTimer <= 0) {
      const dustColor = player.overdriveTimer > 0 ? 0xffc64f : this.level.scenery === 'moon' ? 0x7778a5 : this.level.scenery === 'desert' ? 0xd19a64 : 0x9edb78;
      const dustPosition = TEMP_VECTOR_C.set(
        player.model.root.position.x - forwardX * 1.1,
        0.06,
        player.model.root.position.z - forwardZ * 1.1,
      );
      this.vfx.driveDust(dustPosition, -player.velocity.x * 0.12, -player.velocity.z * 0.12, dustColor, player.speedRatio);
      // 履带印痕与扬尘同节拍同落点（车身后缘）；本方法只为本地玩家调用，远程/AI 不打印（范围克制）。
      this.trails.stampTrack(dustPosition.x, dustPosition.z, player.bodyAngle, 1);
      player.dustTimer = player.overdriveTimer > 0 ? 0.045 : 0.07;
    }
  }

  private updateEnemies(delta: number): void {
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;
      enemy.fireCooldown -= delta;
      enemy.invulnerable = Math.max(0, enemy.invulnerable - delta);
      enemy.hazardTimer = Math.max(0, enemy.hazardTimer - delta);
      enemy.hitFlash = Math.max(0, enemy.hitFlash - delta);
      enemy.aiRetarget -= delta;
      enemy.bossBurstTimer -= delta;
      // PvP 远程坦克：位姿与血量由网络快照逐帧驱动，本地只跑上面的装饰计时，跳过全部 AI。
      if (enemy.remote) continue;

      const target = this.selectEnemyTarget(enemy);
      if (!target) continue;
      const targetPosition = 'model' in target ? target.model.root.position : target;
      const targetX = targetPosition.x;
      const targetZ = targetPosition.z;
      const dx = targetX - enemy.model.root.position.x;
      const dz = targetZ - enemy.model.root.position.z;
      const distance = Math.max(0.001, Math.hypot(dx, dz));
      const targetAngle = Math.atan2(dx, -dz);
      enemy.turretAngle = dampAngle(enemy.turretAngle, targetAngle, enemy.kind === 'sniper' ? 5.2 : 8, delta);
      enemy.model.turret.rotation.y = enemy.bodyAngle - enemy.turretAngle;

      let driveX = dx / distance;
      let driveZ = dz / distance;
      let speedMultiplier = 1;
      if (enemy.kind === 'sniper') {
        if (distance < 7.5) {
          driveX *= -1;
          driveZ *= -1;
        } else if (distance < 11.5) {
          const strafe = enemy.aiTurnDirection;
          const oldX = driveX;
          driveX = -driveZ * strafe;
          driveZ = oldX * strafe;
          speedMultiplier = 0.62;
        }
      }
      if (enemy.kind === 'boss') speedMultiplier = distance < 8 ? 0.18 : 0.62;

      const desiredBodyAngle = Math.atan2(driveX, -driveZ);
      enemy.bodyAngle = dampAngle(enemy.bodyAngle, desiredBodyAngle + enemy.aiBlocked * enemy.aiTurnDirection, enemy.kind === 'bruiser' ? 3.2 : 4.8, delta);
      const forward = forwardFromAngle(enemy.bodyAngle, TEMP_VECTOR);
      const beforeX = enemy.model.root.position.x;
      const beforeZ = enemy.model.root.position.z;
      const hazardSpeed = this.hazardSpeedMultiplierAt(enemy.model.root.position.x, enemy.model.root.position.z);
      this.moveTank(enemy, forward.x * enemy.speed * speedMultiplier * hazardSpeed * delta, forward.z * enemy.speed * speedMultiplier * hazardSpeed * delta);
      const moved = Math.hypot(enemy.model.root.position.x - beforeX, enemy.model.root.position.z - beforeZ);
      if (moved < enemy.speed * delta * 0.2) {
        enemy.aiBlocked = Math.min(1.35, enemy.aiBlocked + delta * 1.7);
        if (Math.random() < 0.025) enemy.aiTurnDirection *= -1;
      } else {
        enemy.aiBlocked = Math.max(0, enemy.aiBlocked - delta * 1.2);
      }
      enemy.model.root.rotation.y = -enemy.bodyAngle;
      enemy.model.antennaTip.scale.setScalar(0.09 + Math.sin(this.elapsed * 5 + enemy.id) * 0.014);

      const lineOfSight = this.hasLineOfSight(enemy.model.root.position.x, enemy.model.root.position.z, targetX, targetZ);
      const fireRange = enemy.kind === 'sniper' ? 18 : enemy.kind === 'boss' ? 19 : 13;
      if (enemy.fireCooldown <= 0 && distance < fireRange && (lineOfSight || enemy.kind === 'boss')) {
        this.fireTank(enemy);
      }

      if (enemy.kind === 'boss') this.updateBoss(enemy);
      this.checkEnemyContact(enemy);
    }
  }

  private updateBoss(boss: TankEntity): void {
    const ratio = boss.health / boss.maxHealth;
    if (ratio <= 0.68 && (boss.bossSummonMask & 1) === 0) {
      boss.bossSummonMask |= 1;
      this.summonBossHelpers(boss, 2);
    }
    if (ratio <= 0.34 && (boss.bossSummonMask & 2) === 0) {
      boss.bossSummonMask |= 2;
      this.summonBossHelpers(boss, 3);
      boss.fireInterval *= 0.72;
      this.ui.showBanner('最终阶段', '皇冠暴走！');
    }
  }

  private summonBossHelpers(boss: TankEntity, count: number): void {
    this.ui.toast('♛', '泡泡王召唤援军', `${count} 辆护卫坦克冲入战场`);
    for (let i = 0; i < count; i += 1) {
      const kind: EnemyKind = i % 2 === 0 ? 'scout' : 'bruiser';
      const angle = i / count * Math.PI * 2;
      const spawn = {
        x: boss.model.root.position.x + Math.cos(angle) * 4.2,
        z: boss.model.root.position.z + Math.sin(angle) * 4.2,
      };
      this.spawnEnemy(kind, spawn, true);
    }
  }

  private selectEnemyTarget(enemy: TankEntity): TankEntity | CoreEntity | null {
    let target: TankEntity | CoreEntity | null = this.core;
    let bestDistance = this.core ? distanceSq2D(enemy.model.root.position, this.core) : Number.POSITIVE_INFINITY;
    for (const player of this.players) {
      if (!player.alive) continue;
      const distance = distanceSq2D(enemy.model.root.position, player.model.root.position);
      const attraction = enemy.kind === 'sniper' || enemy.kind === 'boss' ? 1.25 : 0.72;
      if (distance * attraction < bestDistance) {
        bestDistance = distance;
        target = player;
      }
    }
    return target;
  }

  private fireTank(tank: TankEntity): void {
    const isPlayer = tank.team === 'player';
    // 增益判定不再限 player 队：PvE 敌人增益计时恒为 0（collectPickup 只作用于玩家），行为不变；
    // PvP 远端敌方代理的 rapid/overdrive 经快照 buffs 复现，让对手的超载/速射开火可见（仅视觉，命中仍归射手端）。
    const rapidFactor = tank.rapidTimer > 0 ? PICKUP_TUNING.rapid.fireIntervalFactor : 1;
    const overdrive = tank.overdriveTimer > 0;
    const overdriveFactor = overdrive ? PICKUP_TUNING.overdrive.fireIntervalFactor : 1;
    // 速射与超载的装填加成取较强者、不叠乘：叠乘 0.48×0.68≈0.33（0.18s/发）配合超载 2 伤，
    // 在 PvP 3 血制下双道具凑齐≈秒杀（2026-07-26 平衡钳制；两增益的其余效果照常叠加）。
    tank.fireCooldown = tank.fireInterval * Math.min(rapidFactor, overdriveFactor);
    const muzzlePosition = TEMP_VECTOR_C;
    const muzzleDirection = TEMP_VECTOR_B;
    this.getMuzzlePose(tank, muzzlePosition, muzzleDirection);
    // 先取出炮口坐标（TEMP_VECTOR_C 后续会被复用），PvP 上报 fire 事件要用。
    const muzzleX = muzzlePosition.x;
    const muzzleZ = muzzlePosition.z;
    const strong = tank.kind === 'boss';
    const shots = tank.kind === 'boss' ? (tank.health / tank.maxHealth < 0.35 ? 5 : 3) : 1;
    const spread = tank.kind === 'boss' ? 0.16 : 0;
    for (let i = 0; i < shots; i += 1) {
      const offset = (i - (shots - 1) / 2) * spread;
      const poolKey: ProjectilePoolKey = tank.team === 'player' ? 'player-normal' : strong ? 'enemy-strong' : 'enemy-normal';
      const projectile = this.acquireProjectile(tank.team, strong, poolKey);
      projectile.velocity.copy(muzzleDirection);
      if (offset !== 0) projectile.velocity.applyAxisAngle(UP, -offset);
      projectile.velocity.normalize();
      projectile.root.position.copy(muzzlePosition);
      // 出膛即 prev=cur，防止复用池弹体从回收位拉面条。
      projectile.prevX = muzzlePosition.x;
      projectile.prevZ = muzzlePosition.z;
      projectile.root.rotation.set(0, this.renderYawFromDirection(projectile.velocity), 0);
      projectile.id = ++this.entitySerial;
      projectile.team = tank.team;
      projectile.ownerId = tank.id;
      const visualStrong = strong || overdrive;
      projectile.radius = visualStrong ? 0.28 : 0.22;
      projectile.damage = tank.damage + (overdrive ? PICKUP_TUNING.overdrive.damageBonus : 0);
      projectile.life = strong ? 4.4 : overdrive ? 5.2 : 4.8;
      projectile.bounces = isPlayer ? 1 + (overdrive ? PICKUP_TUNING.overdrive.extraBounces : 0) : 0;
      projectile.age = 0;
      projectile.strong = visualStrong;
      projectile.poolKey = poolKey;
      projectile.velocity.multiplyScalar(tank.projectileSpeed * (overdrive ? PICKUP_TUNING.overdrive.projectileSpeedFactor : 1));
      this.dynamicRoot.add(projectile.root);
      this.projectiles.push(projectile);
    }
    this.vfx.muzzle(muzzlePosition, muzzleDirection, overdrive ? 0xff7a3d : isPlayer ? 0xffd84e : 0xff4d78);
    this.shake = Math.max(this.shake, strong ? 0.28 : overdrive ? 0.15 : isPlayer ? 0.09 : 0.045);
    if (isPlayer) tank.recoil = Math.min(1.35, tank.recoil + (overdrive ? 0.58 : 0.38));
    this.audio.play(isPlayer ? 'shoot' : 'enemyShoot', tank.playerIndex, { x: muzzleX, z: muzzleZ });
    // PvP：自己的开火上报 fire 事件（远端据此复现炮弹副本；命中检测仍由本端负责）。
    if (this.pvpContext && !tank.remote && tank.playerIndex === 0) {
      this.pvpNet?.sendEvent({
        kind: 'fire',
        x: muzzleX,
        z: muzzleZ,
        angle: tank.turretAngle,
        speed: tank.projectileSpeed * (overdrive ? PICKUP_TUNING.overdrive.projectileSpeedFactor : 1),
        damage: tank.damage + (overdrive ? PICKUP_TUNING.overdrive.damageBonus : 0),
        count: 1,
        strong: overdrive,
        // 发送端比赛时钟：接收端据此快进弹道副本，补偿传输延迟（见 spawnRemoteFire）。
        t: this.pvpClockMs(),
      });
    }
  }

  /**
   * PvP 远端开火复现：严格按 fire 事件的出膛点/朝向/弹速/伤害生成弹道副本。
   *
   * 关键口径（2026-07-26 保真度修复）：
   * - 不再借道 fireTank 用插值位姿出弹——快照滞后 ~110ms，快速甩炮时方向明显错位；
   * - 副本继承反弹次数（射手端本地是玩家队炮弹，会在金属墙/边界反弹；副本不反弹
   *   会在首次碰墙后与真实弹道分叉，出现「屏幕上没弹却被打中」）；
   * - event.t 存在时把副本沿飞行方向快进传输延迟量（两端比赛时钟同域；上限 150ms，
   *   分 4 小步查障，撞墙/出界即停），缩小「射手已命中、受害者还没看到炮弹」的时差。
   */
  private spawnRemoteFire(event: Extract<CombatEvent, { kind: 'fire' }>, owner: TankEntity | null): void {
    const team: Team = owner?.team ?? 'enemy';
    const count = Math.min(5, Math.max(1, Math.round(event.count) || 1));
    const spread = count > 1 ? 0.16 : 0;
    const strong = event.strong === true;
    const poolKey: ProjectilePoolKey = team === 'player' ? 'player-normal' : strong ? 'enemy-strong' : 'enemy-normal';
    // 炮口高度取所属坦克模型实测值（y 恒定，滞后无影响）；坦克已离场时用近似值。
    const muzzleY = owner ? owner.model.muzzle.getWorldPosition(TEMP_VECTOR).y : 1.05;
    const forwardMs = typeof event.t === 'number' && Number.isFinite(event.t)
      ? THREE.MathUtils.clamp(this.pvpClockMs() - event.t, 0, 150)
      : 0;
    const halfWidth = this.level.arena.width / 2 - 0.42;
    const halfDepth = this.level.arena.depth / 2 - 0.42;
    for (let i = 0; i < count; i += 1) {
      const offset = (i - (count - 1) / 2) * spread;
      const projectile = this.acquireProjectile(team, strong, poolKey);
      const direction = forwardFromAngle(event.angle, TEMP_VECTOR_B);
      if (offset !== 0) direction.applyAxisAngle(UP, -offset);
      projectile.id = ++this.entitySerial;
      projectile.team = team;
      projectile.ownerId = owner?.id ?? 0;
      projectile.root.position.set(event.x, muzzleY, event.z);
      projectile.root.rotation.set(0, this.renderYawFromDirection(direction), 0);
      projectile.velocity.copy(direction).multiplyScalar(event.speed);
      projectile.radius = strong ? 0.28 : 0.22;
      projectile.damage = event.damage;
      projectile.life = strong ? 5.2 : 4.8;
      // 与射手端同口径的反弹次数（overdrive=strong 时叠加额外反弹），保证弹道两端一致。
      projectile.bounces = 1 + (strong ? PICKUP_TUNING.overdrive.extraBounces : 0);
      projectile.age = 0;
      projectile.strong = strong;
      projectile.poolKey = poolKey;
      // 传输延迟快进：分小步沿速度推进，出界或入障即停（避免穿墙瞬移）。
      if (forwardMs > 0) {
        const step = forwardMs / 1000 / 4;
        for (let s = 0; s < 4; s += 1) {
          const nextX = projectile.root.position.x + projectile.velocity.x * step;
          const nextZ = projectile.root.position.z + projectile.velocity.z * step;
          if (Math.abs(nextX) > halfWidth || Math.abs(nextZ) > halfDepth) break;
          if (this.obstacles.some((obstacle) => obstacle.active && this.pointInObstacle(nextX, nextZ, obstacle, projectile.radius))) break;
          projectile.root.position.x = nextX;
          projectile.root.position.z = nextZ;
          projectile.age += step;
        }
      }
      // 出膛即 prev=cur（快进后的位置），防止复用池弹体拉面条。
      projectile.prevX = projectile.root.position.x;
      projectile.prevZ = projectile.root.position.z;
      this.dynamicRoot.add(projectile.root);
      this.projectiles.push(projectile);
    }
    // 炮口特效在真实出膛点播放（不快进），方向沿事件朝向。
    const flashDirection = forwardFromAngle(event.angle, TEMP_VECTOR_B);
    this.vfx.muzzle(TEMP_VECTOR_C.set(event.x, muzzleY, event.z), flashDirection, strong ? 0xff7a3d : team === 'player' ? 0xffd84e : 0xff4d78);
    // 空间音：远处敌人开火按距离衰减+声像——迷雾里看不见也能听声辨位。
    this.audio.play(team === 'player' ? 'shoot' : 'enemyShoot', owner?.playerIndex ?? 0, { x: event.x, z: event.z });
  }

  private updateDroneSupport(player: TankEntity, delta: number): void {
    void delta;
    if (!player.alive || player.droneTimer <= 0 || player.droneCooldown > 0) return;
    const target = this.findNearestEnemy(player.model.root.position.x, player.model.root.position.z, PICKUP_TUNING.drone.searchRange);
    if (!target) return;
    // PvP：无人机只索敌迷雾中当前可见的敌人（否则等同透视自瞄，设计文档 §6 实体可见性口径）。
    if (this.pvpContext && this.fog && !this.fog.isVisible(target.model.root.position.x, target.model.root.position.z)) return;

    player.droneCooldown = PICKUP_TUNING.drone.shotCooldownSec;
    const origin = TEMP_VECTOR_C;
    const direction = TEMP_VECTOR_B;
    player.model.drone.getWorldPosition(origin);
    direction.set(target.model.root.position.x - origin.x, 0, target.model.root.position.z - origin.z);
    if (direction.lengthSq() < 0.0001) return;
    direction.normalize();

    const projectile = this.acquireProjectile('player', false, 'player-normal');
    projectile.id = ++this.entitySerial;
    projectile.team = 'player';
    projectile.ownerId = player.id;
    projectile.root.position.copy(origin);
    // 出膛即 prev=cur（同 fireTank）。
    projectile.prevX = origin.x;
    projectile.prevZ = origin.z;
    projectile.root.rotation.set(0, this.renderYawFromDirection(direction), 0);
    projectile.velocity.copy(direction).multiplyScalar(PICKUP_TUNING.drone.shotSpeed);
    projectile.radius = PICKUP_TUNING.drone.shotRadius;
    projectile.damage = PICKUP_TUNING.drone.shotDamage;
    projectile.life = PICKUP_TUNING.drone.shotLifeSec;
    projectile.bounces = 0;
    projectile.age = 0;
    projectile.strong = false;
    projectile.poolKey = 'player-normal';
    this.dynamicRoot.add(projectile.root);
    this.projectiles.push(projectile);
    this.vfx.muzzle(origin, direction, 0xb46dff);
    this.audio.play('shoot', player.playerIndex, { x: origin.x, z: origin.z });
    // PvP：无人机开火上报 drone 事件（远端据此生成视觉弹道副本；命中检测仍由本端负责）。
    if (this.pvpContext && !player.remote && player.playerIndex === 0) {
      this.pvpNet?.sendEvent({
        kind: 'drone',
        x: origin.x,
        z: origin.z,
        angle: Math.atan2(direction.x, -direction.z),
      });
    }
  }

  /** PvP 远端无人机弹道副本：与 fire 事件复现同口径（视觉副本，命中由射手端检测上报 hit）。 */
  private spawnRemoteDroneShot(x: number, z: number, angle: number, owner: TankEntity | null): void {
    const team: Team = owner?.team ?? 'enemy';
    const poolKey: ProjectilePoolKey = team === 'player' ? 'player-normal' : 'enemy-normal';
    const projectile = this.acquireProjectile(team, false, poolKey);
    projectile.id = ++this.entitySerial;
    projectile.team = team;
    projectile.ownerId = owner?.id ?? 0;
    projectile.root.position.set(x, 1.9, z);
    projectile.prevX = x;
    projectile.prevZ = z;
    const direction = TEMP_VECTOR_B;
    forwardFromAngle(angle, direction);
    projectile.root.rotation.set(0, this.renderYawFromDirection(direction), 0);
    projectile.velocity.copy(direction).multiplyScalar(PICKUP_TUNING.drone.shotSpeed);
    projectile.radius = PICKUP_TUNING.drone.shotRadius;
    projectile.damage = PICKUP_TUNING.drone.shotDamage;
    projectile.life = PICKUP_TUNING.drone.shotLifeSec;
    projectile.bounces = 0;
    projectile.age = 0;
    projectile.strong = false;
    projectile.poolKey = poolKey;
    this.dynamicRoot.add(projectile.root);
    this.projectiles.push(projectile);
    this.vfx.muzzle(projectile.root.position, direction, 0xb46dff);
    this.audio.play(team === 'player' ? 'shoot' : 'enemyShoot', owner?.playerIndex ?? 0, { x, z });
  }

  private getMuzzlePose(tank: TankEntity, position: any, direction: any): void {
    tank.model.muzzle.getWorldPosition(position);
    tank.model.muzzle.getWorldQuaternion(TEMP_QUATERNION);
    direction.set(0, 0, -1).applyQuaternion(TEMP_QUATERNION);
    direction.y = 0;
    if (direction.lengthSq() < 0.000001) forwardFromAngle(tank.turretAngle, direction);
    else direction.normalize();
  }

  private renderYawFromDirection(direction: any): number {
    return Math.atan2(-direction.x, -direction.z);
  }

  private acquireProjectile(team: Team, strong: boolean, poolKey: ProjectilePoolKey): ProjectileEntity {
    const pooled = this.projectilePool[poolKey].pop();
    if (pooled) {
      pooled.root.visible = true;
      pooled.root.position.set(0, 0, 0);
      pooled.root.rotation.set(0, 0, 0);
      return pooled;
    }
    return {
      id: 0,
      team,
      ownerId: 0,
      root: this.models.createProjectile(team, strong).root,
      velocity: new THREE.Vector3(),
      radius: 0,
      damage: 0,
      life: 0,
      bounces: 0,
      age: 0,
      strong,
      poolKey,
      prevX: 0,
      prevZ: 0,
      curX: 0,
      curZ: 0,
    };
  }

  private updateWave(delta: number): void {
    if (this.wave.queue.length > 0) {
      this.wave.spawnTimer -= delta;
      if (this.wave.spawnTimer <= 0) {
        const kind = this.wave.queue.shift()!;
        this.spawnEnemy(kind);
        const waveSpec = this.level.waves[this.wave.index];
        this.wave.spawnTimer = waveSpec.interval / DIFFICULTY_TUNING[this.selection.difficulty].spawnRate;
      }
      return;
    }

    let livingEnemies = 0;
    for (const enemy of this.enemies) if (enemy.alive) livingEnemies += 1;
    if (livingEnemies > 0) return;

    if (this.wave.index >= this.level.waves.length - 1) {
      this.wave.completionTimer += delta;
      return;
    }

    this.wave.transitionTimer -= delta;
    if (this.wave.transitionTimer <= 0) {
      this.wave.index += 1;
      const spec = this.level.waves[this.wave.index];
      this.wave.queue = [...spec.enemies];
      this.wave.spawnTimer = 0.15;
      this.wave.transitionTimer = spec.pauseAfter;
      this.ui.showBanner(spec.label.toUpperCase(), `第 ${this.wave.index + 1} 波`);
      this.audio.play('wave');
    }
  }

  private spawnEnemy(kind: EnemyKind, forcedSpawn?: Vec2, summoned = false): TankEntity {
    const tuning = DIFFICULTY_TUNING[this.selection.difficulty];
    const baseStats: Record<EnemyKind, { health: number; speed: number; interval: number; projectile: number; radius: number; damage: number }> = {
      scout: { health: 1, speed: 3.65, interval: 1.75, projectile: 10.5, radius: 1.02, damage: 1 },
      bruiser: { health: 3, speed: 2.35, interval: 2.25, projectile: 9, radius: 1.32, damage: 1 },
      sniper: { health: 2, speed: 2.15, interval: 2.65, projectile: 13.5, radius: 1.08, damage: 1 },
      boss: { health: 18, speed: 1.85, interval: 1.55, projectile: 11.3, radius: 2.08, damage: 1 },
    };
    const stats = baseStats[kind];
    const spawnOptions = forcedSpawn ? [forcedSpawn] : this.level.enemySpawns;
    const spawn = spawnOptions[Math.floor(Math.random() * spawnOptions.length)];
    const healthScale = kind === 'boss' ? Math.max(1, tuning.enemyHealth * 0.92) : tuning.enemyHealth;
    const maxHealth = Math.max(1, Math.round(stats.health * healthScale));
    const model = this.models.createTank(this.enemyPalette(kind), kind);
    model.root.position.set(spawn.x, 0, spawn.z);
    model.root.rotation.y = 0;
    model.root.scale.setScalar(kind === 'boss' ? 1.12 : 1);
    this.dynamicRoot.add(model.root);
    const enemy: TankEntity = {
      id: ++this.entitySerial,
      team: 'enemy',
      kind,
      playerIndex: -1,
      model,
      velocity: new THREE.Vector3(),
      bodyAngle: 0,
      turretAngle: 0,
      health: maxHealth,
      maxHealth,
      lives: 1,
      radius: stats.radius,
      speed: stats.speed * tuning.enemySpeed,
      fireCooldown: summoned ? 1.4 : 1.1 + Math.random() * 0.8,
      fireInterval: stats.interval / tuning.enemyFireRate,
      projectileSpeed: stats.projectile,
      damage: stats.damage,
      alive: true,
      respawnTimer: 0,
      invulnerable: 0.55,
      shieldTimer: 0,
      rapidTimer: 0,
      overdriveTimer: 0,
      droneTimer: 0,
      droneCooldown: 0,
      hazardTimer: 0,
      inputIntensity: 0,
      speedRatio: 0,
      handlingLean: 0,
      handlingPitch: 0,
      recoil: 0,
      dustTimer: 0,
      hitFlash: 0,
      spawnPoint: { ...spawn },
      aiBlocked: 0,
      aiTurnDirection: Math.random() > 0.5 ? 1 : -1,
      aiRetarget: 0,
      targetId: null,
      bossSummonMask: 0,
      bossBurstTimer: 0,
      spawnAngle: 0,
      // 出生即 prev=cur，防止插值从旧位置拉面条。
      prevX: spawn.x,
      prevZ: spawn.z,
      prevBody: 0,
      prevTurret: 0,
      curX: spawn.x,
      curZ: spawn.z,
    };
    this.enemies.push(enemy);
    TEMP_VECTOR_C.copy(model.root.position).y = 0.2;
    this.vfx.spawn(TEMP_VECTOR_C, kind === 'boss' ? 0xffd34f : 0xff5b7a);
    if (kind === 'boss') {
      this.ui.toast('♛', '皇冠巨坦登场', '小心散射炮弹与召唤护卫');
      this.shake = 0.55;
    }
    return enemy;
  }

  private updateProjectiles(delta: number): void {
    for (let index = this.projectiles.length - 1; index >= 0; index -= 1) {
      const projectile = this.projectiles[index];
      projectile.life -= delta;
      projectile.age += delta;
      if (projectile.life <= 0) {
        this.removeProjectile(index);
        continue;
      }

      const previousX = projectile.root.position.x;
      const previousZ = projectile.root.position.z;
      projectile.root.position.addScaledVector(projectile.velocity, delta);
      projectile.root.rotation.y = this.renderYawFromDirection(projectile.velocity);
      projectile.root.rotation.z += delta * 8;

      // 拖尾泡：PvP 迷雾中不可见的敌方炮弹不打点（否则轨迹暴露射手位置）。
      if (projectile.team !== 'enemy' || !this.fog || this.fog.isVisible(projectile.root.position.x, projectile.root.position.z)) {
        this.trails.emitTrail(
          projectile.root.position.x,
          projectile.root.position.y,
          projectile.root.position.z,
          projectile.team === 'player' ? 0xffd84e : 0xff4d78,
          projectile.strong ? 1.4 : 1,
        );
      }

      if (this.handleProjectileBoundary(projectile, index)) continue;

      let consumed = false;
      for (const obstacle of this.obstacles) {
        if (!obstacle.active) continue;
        if (!this.pointInObstacle(projectile.root.position.x, projectile.root.position.z, obstacle, projectile.radius)) continue;
        // 反弹只看 bounces（PvE 敌弹恒 0，行为不变；PvP 对手弹道副本带反弹数，与射手端弹道一致）。
        if (obstacle.kind === 'metal' && projectile.bounces > 0) {
          this.reflectProjectile(projectile, obstacle, previousX, previousZ);
          projectile.bounces -= 1;
          this.vfx.impact(projectile.root.position, 0xffdf55, false);
          this.audio.play('hit', 0, { x: projectile.root.position.x, z: projectile.root.position.z });
        } else {
          if (obstacle.kind === 'crate') this.damageObstacle(obstacle, projectile.damage);
          this.vfx.impact(projectile.root.position, obstacle.kind === 'metal' ? 0x79dfff : 0xffb05f, projectile.strong);
          this.removeProjectile(index);
          consumed = true;
        }
        break;
      }
      if (consumed) continue;

      if (projectile.team === 'player') {
        for (const enemy of this.enemies) {
          if (!enemy.alive || enemy.invulnerable > 0) continue;
          const hitDistance = projectile.radius + enemy.radius;
          if (distanceSq2D(projectile.root.position, enemy.model.root.position) <= hitDistance * hitDistance) {
            // PvP：射击者检测命中并上报 hit，伤害由受害者客户端执行（本地不调 damageTank）。
            if (this.pvpContext) this.reportPvpHit(projectile, enemy);
            else this.damageTank(enemy, projectile.damage, projectile);
            this.removeProjectile(index);
            consumed = true;
            break;
          }
        }
      } else {
        for (const player of this.players) {
          if (!player.alive || player.invulnerable > 0) continue;
          const hitDistance = projectile.radius + player.radius;
          if (distanceSq2D(projectile.root.position, player.model.root.position) <= hitDistance * hitDistance) {
            // PvP：敌方炮弹只是远程副本，命中只播特效——自己的血量由收到的 hit 事件扣减。
            if (this.pvpContext) this.reportPvpHit(projectile, player);
            else this.damageTank(player, projectile.damage, projectile);
            this.removeProjectile(index);
            consumed = true;
            break;
          }
        }
        if (!consumed && this.core) {
          const coreDistance = projectile.radius + 1.45;
          if (distanceSq2D(projectile.root.position, this.core) <= coreDistance * coreDistance) {
            this.damageCore(projectile.damage);
            this.vfx.impact(projectile.root.position, 0xffd54f, true);
            this.removeProjectile(index);
            consumed = true;
          }
        }
      }
    }
  }

  private handleProjectileBoundary(projectile: ProjectileEntity, index: number): boolean {
    const halfWidth = this.level.arena.width / 2 - 0.42;
    const halfDepth = this.level.arena.depth / 2 - 0.42;
    let bounced = false;
    if (projectile.root.position.x < -halfWidth || projectile.root.position.x > halfWidth) {
      if (projectile.bounces > 0) {
        projectile.root.position.x = THREE.MathUtils.clamp(projectile.root.position.x, -halfWidth, halfWidth);
        projectile.velocity.x *= -1;
        projectile.bounces -= 1;
        bounced = true;
      } else {
        this.removeProjectile(index);
        return true;
      }
    }
    if (projectile.root.position.z < -halfDepth || projectile.root.position.z > halfDepth) {
      if (projectile.bounces > 0) {
        projectile.root.position.z = THREE.MathUtils.clamp(projectile.root.position.z, -halfDepth, halfDepth);
        projectile.velocity.z *= -1;
        projectile.bounces -= 1;
        bounced = true;
      } else {
        this.removeProjectile(index);
        return true;
      }
    }
    if (bounced) {
      this.vfx.impact(projectile.root.position, 0xffe26b, false);
      this.audio.play('hit', 0, { x: projectile.root.position.x, z: projectile.root.position.z });
    }
    return false;
  }

  private reflectProjectile(projectile: ProjectileEntity, obstacle: ObstacleEntity, previousX: number, previousZ: number): void {
    const cos = Math.cos(-obstacle.rotation);
    const sin = Math.sin(-obstacle.rotation);
    const localPrevX = (previousX - obstacle.x) * cos - (previousZ - obstacle.z) * sin;
    const localPrevZ = (previousX - obstacle.x) * sin + (previousZ - obstacle.z) * cos;
    const halfWidth = obstacle.width / 2 + projectile.radius;
    const halfDepth = obstacle.depth / 2 + projectile.radius;
    const hitX = Math.abs(localPrevX) > halfWidth * 0.82;
    const localVelocityX = projectile.velocity.x * cos - projectile.velocity.z * sin;
    const localVelocityZ = projectile.velocity.x * sin + projectile.velocity.z * cos;
    const reflectedX = hitX ? -localVelocityX : localVelocityX;
    const reflectedZ = hitX ? localVelocityZ : -localVelocityZ;
    const worldCos = Math.cos(obstacle.rotation);
    const worldSin = Math.sin(obstacle.rotation);
    projectile.velocity.x = reflectedX * worldCos - reflectedZ * worldSin;
    projectile.velocity.z = reflectedX * worldSin + reflectedZ * worldCos;
    projectile.root.position.x = previousX + projectile.velocity.x * 0.012;
    projectile.root.position.z = previousZ + projectile.velocity.z * 0.012;
  }

  private damageObstacle(obstacle: ObstacleEntity, damage: number): void {
    if (!obstacle.active || obstacle.kind !== 'crate') return;
    obstacle.health -= damage;
    const ratio = Math.max(0.45, obstacle.health / obstacle.maxHealth);
    obstacle.root.scale.y = ratio;
    obstacle.root.rotation.z = (Math.random() - 0.5) * 0.08;
    if (obstacle.health <= 0) {
      obstacle.active = false;
      this.gameplayRoot.remove(obstacle.root);
      // PvP 迷雾：Crate 摧毁后清除其挡视线格，迷雾随破坏开阔。
      this.fog?.unblockArea({ x: obstacle.x, z: obstacle.z, halfW: obstacle.width / 2, halfD: obstacle.depth / 2, rotation: obstacle.rotation });
      this.vfx.crateBreak(TEMP_VECTOR_C.set(obstacle.x, 0.5, obstacle.z), this.level.scenery === 'moon' ? 0xb96dcb : 0xd99654);
      this.audio.play('explode', 0, { x: obstacle.x, z: obstacle.z });
      this.score += 30;
    }
  }

  /**
   * PvP 炮弹命中坦克的本地处理（设计文档 §2 伤害权威模型：射击者检测、受害者扣血）：
   * 自己发射的炮弹命中 → 上报 hit 事件；远程实体的炮弹副本 → 仅本地特效。
   * 两种情形都不调 damageTank——远程坦克血量以快照为准，自己的血量等对方上报。
   */
  private reportPvpHit(projectile: ProjectileEntity, target: TankEntity): void {
    const self = this.selfTank();
    if (self && projectile.ownerId === self.id && target.remotePlayerId) {
      this.pvpNet?.sendEvent({ kind: 'hit', targetId: target.remotePlayerId, damage: projectile.damage });
      // 即时命中反馈：受击闪白（血量下降约 130ms 后才随快照到达，先给视觉确认）
      target.hitFlash = 0.14;
    }
    // 与 damageTank 同口径的分级反馈；远端代理血量是快照滞后值，按「受击后 ≈ 当前 − 本发伤害」估算残血档。
    const impactTier = projectile.strong || target.health - projectile.damage <= 1 ? 'critical' : 'normal';
    this.vfx.impactGraded(projectile.root.position, target.team === 'player' ? 0x68eaff : 0xff685f, impactTier);
    this.audio.play('hit', 0, { x: projectile.root.position.x, z: projectile.root.position.z });
  }

  private damageTank(tank: TankEntity, damage: number, projectile?: ProjectileEntity): void {
    // PvP：远程坦克血量以其所属客户端的快照为准，本地伤害判定（炮弹/地雷/危险区/接触）一律不生效。
    if (this.pvpContext && tank.remote) return;
    if (!tank.alive || tank.invulnerable > 0) return;
    if (tank.shieldTimer > 0) {
      tank.shieldTimer = Math.max(0, tank.shieldTimer - 1.2);
      TEMP_VECTOR_C.copy(tank.model.root.position).y = 0.8;
      this.vfx.shieldHit(TEMP_VECTOR_C, tank.team === 'player' ? 0x5cecff : 0xff6c95);
      this.audio.play('shield', 0, { x: tank.model.root.position.x, z: tank.model.root.position.z });
      return;
    }
    tank.health -= damage;
    tank.invulnerable = tank.team === 'player' ? 0.48 : 0.08;
    tank.hitFlash = 0.18;
    TEMP_VECTOR_C.copy(tank.model.root.position).y = 0.65;
    // 命中反馈分级：强化弹或受击后残血（health ≤ 1）升 critical；环境伤害/地雷无 projectile 走 normal。
    const impactTier = (projectile?.strong ?? false) || tank.health <= 1 ? 'critical' : 'normal';
    this.vfx.impactGraded(TEMP_VECTOR_C, tank.team === 'player' ? 0x68eaff : 0xff685f, impactTier);
    this.audio.play('hit', 0, { x: tank.model.root.position.x, z: tank.model.root.position.z });
    this.shake = Math.max(this.shake, tank.kind === 'boss' ? 0.18 : 0.12);
    if (tank.team === 'player') this.ui.flashImpact(false);
    if (tank.health <= 0) this.destroyTank(tank);
  }

  private destroyTank(tank: TankEntity): void {
    if (!tank.alive) return;
    tank.alive = false;
    tank.model.root.visible = false;
    TEMP_VECTOR_C.copy(tank.model.root.position).y = 0.45;
    // 击杀爆发分级：killBlast（爆炸+金闪+双环+亮屑喷泉）取代普通爆炸，PvP 与 PvE 都生效；
    // boss 保留其 1.9 倍特大爆炸，再叠加 killBlast 的金色演出。
    const killColor = tank.team === 'player' ? 0x66dfff : tank.kind === 'boss' ? 0xffce4f : 0xff7358;
    if (tank.kind === 'boss') this.vfx.explosion(TEMP_VECTOR_C, killColor, 1.9);
    this.vfx.killBlast(TEMP_VECTOR_C, killColor);
    this.audio.play('explode', 0, { x: tank.model.root.position.x, z: tank.model.root.position.z });
    this.shake = Math.max(this.shake, tank.kind === 'boss' ? 0.82 : 0.35);

    if (this.pvpContext) {
      if (!tank.remote && tank.playerIndex === 0) {
        // 自己阵亡：lives 保持 1 占位（pvp 无限重生），按规则秒数重生，并广播 died（死亡只能自己宣布）。
        const rule = MATCH_RULES[this.pvpContext.mode];
        tank.respawnTimer = rule.respawnSec;
        tank.velocity.set(0, 0, 0);
        const killerId = this.pvpLastAttacker;
        this.pvpNet?.sendEvent({ kind: 'died', killerId });
        this.pvpLastAttacker = null;
        // 本地比分缓存同步入账：自己的 died 不会被服务器回转（relay 排除发送者），
        // 对方这一杀若不在此入账，HUD 比分就只涨自己击杀、永远看不到对方击杀（2026-07-26 修复）。
        const killer = killerId
          ? this.pvpContext.members.find((member) => member.playerId === killerId)
          : null;
        if (killer && killer.team !== this.pvpContext.selfTeam) {
          this.pvpScore[killer.team === 'alpha' ? 0 : 1] += 1;
          const killerName = this.pvpNames.get(killer.playerId) ?? '对手';
          this.ui.toast('×', '击杀播报', `${killerName} 击毁了你`);
        }
        this.ui.toast('♥', '坦克正在重组', `${rule.respawnSec} 秒后返回战场`);
        this.ui.flashImpact(true);
        // First Blood / 连杀阶梯记账（本端阵亡路径；远端死亡走 handlePvpEvent）。
        this.registerPvpKill(killerId, this.pvpContext.selfId);
      }
      // 远程坦克正常走不到这里（damageTank 已守卫）；保险路径只保留上面的爆炸演出，不动计分与重生。
      return;
    }

    if (tank.team === 'player') {
      tank.lives -= 1;
      tank.respawnTimer = tank.lives > 0 ? 1.8 : 0;
      tank.velocity.set(0, 0, 0);
      this.ui.toast('♥', tank.lives > 0 ? '坦克正在重组' : '坦克已退场', tank.lives > 0 ? '1.8 秒后返回战场' : '剩余队友仍可继续作战');
      this.ui.flashImpact(true);
    } else {
      this.defeated += 1;
      const points: Record<EnemyKind, number> = { scout: 100, bruiser: 220, sniper: 180, boss: 1500 };
      const tuning = DIFFICULTY_TUNING[this.selection.difficulty];
      this.combo = this.comboTimer > 0 ? Math.min(9, this.combo + 1) : 1;
      this.maxCombo = Math.max(this.maxCombo, this.combo);
      this.comboTimer = 3.25;
      this.score += Math.round(points[tank.kind as EnemyKind] * this.combo * tuning.scoreMultiplier);
      if (this.combo >= 3) this.ui.toast('×', `${this.combo} 连击！`, '继续进攻，倍率正在燃烧');
      if (tank.kind !== 'boss' && Math.random() < (this.selection.mode === 'coop' ? 0.31 : 0.23)) this.spawnPickupAt(tank.model.root.position.x, tank.model.root.position.z);
      if (tank.kind === 'boss') this.ui.showBanner('KING DOWN', '泡泡王倒下！');
      // PvE enemies never respawn. Remote PvP tanks return through the branch above.
      if (!tank.remote) this.enemyReclaimQueue.push(tank);
    }
  }

  /**
   * Remove dead PvE enemies from both the scene graph and the active array.
   *
   * The old path only set visible=false. Object3D.updateMatrixWorld still traverses
   * invisible descendants, so every dead tank retained roughly 60 matrix updates,
   * while AI, collision, synchronization, and camera loops scanned an ever-growing
   * array. A 50-second combat run degraded from 10.4ms to 48.6ms per frame.
   *
   * Do not dispose here: ModelFactory and MaterialLibrary resources are shared and
   * persistent, so disposing one dead tank would invalidate live tanks.
   */
  private reclaimDeadEnemies(): void {
    if (this.enemyReclaimQueue.length === 0) return;
    for (const tank of this.enemyReclaimQueue) {
      this.dynamicRoot.remove(tank.model.root);
      const index = this.enemies.indexOf(tank);
      if (index >= 0) this.enemies.splice(index, 1);
    }
    this.enemyReclaimQueue.length = 0;
  }

  private respawnPlayer(player: TankEntity): void {
    player.alive = true;
    player.health = player.maxHealth;
    player.invulnerable = 2.4;
    player.shieldTimer = 2.4;
    player.model.root.visible = true;
    player.model.root.position.set(player.spawnPoint.x, 0, player.spawnPoint.z);
    // 恢复出生朝向（PvE 恒为 Math.PI；PvP 出生点面向场地中央）。
    player.bodyAngle = player.spawnAngle;
    player.turretAngle = player.spawnAngle;
    player.inputIntensity = 0;
    player.speedRatio = 0;
    player.handlingLean = 0;
    player.handlingPitch = 0;
    player.recoil = 0;
    player.dustTimer = 0;
    player.model.root.rotation.set(0, -player.spawnAngle, 0);
    player.model.root.position.y = 0;
    player.velocity.set(0, 0, 0);
    // 重生瞬移：prev=cur，防止插值从死亡点拉面条回出生点。
    this.captureTankPrev(player);
    TEMP_VECTOR_C.copy(player.model.root.position).y = 0.2;
    this.vfx.spawn(TEMP_VECTOR_C, player.playerIndex === 0 ? 0xffd84d : 0x58e5ff);
    this.audio.play('shield');
  }

  private updatePickups(delta: number): void {
    for (let index = this.pickups.length - 1; index >= 0; index -= 1) {
      const pickup = this.pickups[index];
      pickup.life -= delta;
      pickup.phase += delta;
      if (pickup.life <= 0) {
        this.dynamicRoot.remove(pickup.root);
        this.pickups.splice(index, 1);
        continue;
      }
      pickup.root.rotation.y += delta * 1.2;
      pickup.root.position.y = Math.sin(pickup.phase * 2.7) * 0.12;
      pickup.core.rotation.x += delta * 1.8;
      pickup.core.rotation.z += delta * 2.2;
      pickup.orbiters.forEach((orbiter, orbiterIndex) => {
        const angle = pickup.phase * 2 + orbiter.userData.orbitOffset;
        orbiter.position.x = Math.cos(angle) * 0.7;
        orbiter.position.z = Math.sin(angle) * 0.7;
        orbiter.rotation.y = -angle + orbiterIndex;
      });

      // PvP：远程代理（队友与对手）也参与拾取判定——他们在所属客户端权威捡到后，
      // 本地按快照位置同步移除，保持共享世界一致（增益/布雷不在本地生效，见 collectPickup）；
      // PvE 敌人不吃道具，维持只扫玩家。复用 scratch 与 updateHazards 错峰（进入即清空）。
      const collectors = this.activeTanksScratch;
      collectors.length = 0;
      for (const player of this.players) collectors.push(player);
      if (this.pvpContext) for (const enemy of this.enemies) collectors.push(enemy);
      for (const tank of collectors) {
        if (!tank.alive) continue;
        if (distanceSq2D(tank.model.root.position, pickup) < 1.65 * 1.65) {
          this.collectPickup(tank, pickup);
          this.dynamicRoot.remove(pickup.root);
          this.pickups.splice(index, 1);
          break;
        }
      }
    }
  }

  private spawnPickupAt(x: number, z: number, forcedKind?: PickupKind, life = 13): boolean {
    // 同屏上限按模式分档：PvP 大地图（58×42，约 3.9 倍于第 1 关）此前误用单人档 5，
    // 把第 14 轮「5s 槽位加密补给」的意图静默掐掉（2026-07-26 修复为 8）。
    const pickupCap = this.selection.mode === 'pvp' ? 8 : this.selection.mode === 'coop' ? 7 : 5;
    if (this.pickups.length >= pickupCap) return false;
    const kind = forcedKind ?? this.choosePickupKind(PICKUP_KINDS, Math.random);
    const model = this.models.createPickup(kind);
    model.root.position.set(x, 0, z);
    this.dynamicRoot.add(model.root);
    this.pickups.push({
      id: ++this.entitySerial,
      kind,
      root: model.root,
      orbiters: model.orbiters,
      core: model.core,
      x,
      z,
      life,
      phase: Math.random() * Math.PI * 2,
      prevRotY: 0,
      prevY: 0,
      curRotY: 0,
      curY: 0,
    });
    return true;
  }

  private collectPickup(player: TankEntity, pickup: PickupEntity): void {
    const info = PICKUP_INFO[pickup.kind];
    // PvP 远程代理的拾取：增益/布雷/计分由所属客户端权威执行（快照与 mine 事件同步回来），
    // 本地只播消失特效——否则 mine 会本地重复布雷、增益计时与快照打架、toast 冒出 PvE 角色名。
    if (player.remote) {
      this.vfx.pickup(TEMP_VECTOR_C.set(pickup.x, 0.75, pickup.z), info.color);
      this.audio.play('pickup', 0, { x: pickup.x, z: pickup.z });
      return;
    }
    if (pickup.kind === 'repair') player.health = Math.min(player.maxHealth, player.health + PICKUP_TUNING.repair.heal);
    if (pickup.kind === 'shield') player.shieldTimer = Math.max(player.shieldTimer, PICKUP_TUNING.shield.durationSec);
    if (pickup.kind === 'rapid') player.rapidTimer = Math.max(player.rapidTimer, PICKUP_TUNING.rapid.durationSec);
    if (pickup.kind === 'overdrive') player.overdriveTimer = Math.max(player.overdriveTimer, PICKUP_TUNING.overdrive.durationSec);
    if (pickup.kind === 'mine') {
      const mineX = player.model.root.position.x;
      const mineZ = player.model.root.position.z;
      this.spawnMine(mineX, mineZ, player.id);
      // PvP：本地布雷上报 mine 事件，远端据此生成地雷副本。
      if (this.pvpContext && !player.remote && player.playerIndex === 0) {
        this.pvpNet?.sendEvent({ kind: 'mine', x: mineX, z: mineZ });
      }
    }
    if (pickup.kind === 'drone') {
      player.droneTimer = Math.max(player.droneTimer, PICKUP_TUNING.drone.durationSec);
      player.droneCooldown = Math.min(player.droneCooldown, PICKUP_TUNING.drone.resumeCooldownSec);
    }
    this.vfx.pickup(TEMP_VECTOR_C.set(pickup.x, 0.75, pickup.z), info.color);
    this.audio.play('pickup', 0, { x: pickup.x, z: pickup.z });
    this.score += 75;
    this.ui.toast(info.icon, info.name, `${this.pvpContext ? '你' : player.playerIndex === 0 ? '柠檬先锋' : '莓果搭档'}：${info.effect}`);
  }

  private updatePickupSpots(delta: number): void {
    if (this.pickupSpots.length === 0) return;
    for (const spot of this.pickupSpots) {
      spot.root.rotation.y += delta * 0.55;
      spot.root.scale.setScalar(1 + Math.sin(this.elapsed * 2.4 + spot.id) * 0.035);
    }
    // PvP 共享世界刷新走比赛时钟槽位（与本地实体状态解耦，两端可自愈重同步）。
    if (this.pvpContext) {
      this.updatePvpPickupSpawn();
      return;
    }
    this.pickupSpawnTimer -= delta;
    if (this.pickupSpawnTimer > 0) return;
    const spawned = this.spawnPickupAtRandomSpot(Math.random);
    // 2026-07-26：刷新间隔加密（原 6.8+3.4s），配合进攻型道具权重上调，场上补给更密集。
    this.pickupSpawnTimer = spawned ? 5.4 + Math.random() * 2.8 : 1.2;
  }

  /**
   * PvP 拾取物刷新：按比赛时钟固定槽位驱动，每个槽位用「种子基 + 槽号」独立播种。
   * 旧实现两端共用一条种子流、抽取次数依赖本地状态（可用点位/是否刷出），
   * 一次拾取竞态就永久失同步；槽位独立播种后，本地状态分叉只影响当槽，下一槽自愈。
   */
  private updatePvpPickupSpawn(): void {
    if (this.pvpResult || this.pvpInputLocked()) return;
    const slot = Math.floor(this.pvpClockMs() / PVP_PICKUP_SLOT_MS);
    if (slot <= this.pvpPickupSlotHandled) return;
    this.pvpPickupSlotHandled = slot;
    const rng = mulberry32(hashSeed(`${this.pvpSeedBase}:pickup:${slot}`));
    this.spawnPickupAtRandomSpot(rng);
  }

  /** 加权选点并生成拾取物；随机源由调用方注入（PvE = Math.random，PvP = 槽位种子，两端一致）。 */
  private spawnPickupAtRandomSpot(rng: () => number): boolean {
    const available = this.pickupSpots.filter((spot) => (
      !this.pickups.some((pickup) => distanceSq2D(pickup, spot) < 2.3 * 2.3)
    ));
    if (available.length === 0) return false;

    const totalWeight = available.reduce((sum, spot) => sum + spot.weight, 0);
    // 选点影响共享世界，PvP 使用槽位种子（两端一致）
    let roll = rng() * totalWeight;
    let selected = available[available.length - 1];
    for (const spot of available) {
      roll -= spot.weight;
      if (roll <= 0) {
        selected = spot;
        break;
      }
    }
    return this.spawnPickupAt(selected.x, selected.z, this.choosePickupKind(selected.kinds, rng), 17);
  }

  private choosePickupKind(kinds: PickupKind[], rng: () => number): PickupKind {
    // 2026-07-26：drone 回归 PvP 掉落池——无人机开火走 drone 事件复现弹道、
    // 快照 buffs 复现环绕模型，且索敌只认迷雾可见目标（不再透视自瞄），此前的剔除理由已消解。
    const pool = kinds;
    if (pool.length === 0) return 'repair'; // 防御：空点位兜底（当前无此配置）
    // 权重集中在 pickup-info.ts（PvP 槽位种子抽取要求两端权重一致，改动即视为平衡改版）。
    const total = pool.reduce((sum, kind) => sum + PICKUP_WEIGHTS[kind], 0);
    // 种类抽取影响共享世界，PvP 使用槽位种子（两端一致）
    let roll = rng() * total;
    for (const kind of pool) {
      roll -= PICKUP_WEIGHTS[kind];
      if (roll <= 0) return kind;
    }
    return pool[pool.length - 1] ?? 'repair';
  }

  private spawnMine(x: number, z: number, ownerId: number): void {
    const root = this.models.createMine(this.level.palette.danger);
    root.position.set(x, 0, z);
    this.dynamicRoot.add(root);
    // 布雷时解析布雷者队伍与 playerId：同队免疫与 PvP 击杀归因都按布雷时刻快照（之后离场也不变）。
    const owner = [...this.players, ...this.enemies].find((tank) => tank.id === ownerId) ?? null;
    this.mines.push({
      id: ++this.entitySerial,
      ownerId,
      ownerTeam: owner?.team ?? null,
      ownerPlayerId: owner?.remotePlayerId ?? null,
      root,
      x,
      z,
      radius: PICKUP_TUNING.mine.triggerRadius,
      life: PICKUP_TUNING.mine.lifeSec,
      armed: PICKUP_TUNING.mine.armSec,
      phase: Math.random() * Math.PI * 2,
      prevRotY: 0,
      curRotY: 0,
    });
  }

  private updateMines(delta: number): void {
    for (let index = this.mines.length - 1; index >= 0; index -= 1) {
      const mine = this.mines[index];
      mine.life -= delta;
      mine.armed = Math.max(0, mine.armed - delta);
      mine.phase += delta;
      mine.root.rotation.y += delta * 1.5;
      mine.root.scale.setScalar(1 + Math.sin(mine.phase * 5.5) * 0.045);
      if (mine.life <= 0) {
        this.removeMine(index, false);
        continue;
      }
      if (mine.armed > 0) continue;

      // 同队免疫（PvE 语义：地雷只伤敌队——PvE 雷恒属玩家队、敌人必触发，行为不变；
      // PvP：布雷者自己/队友踩上不炸）。ownerTeam 为 null（布雷者离场）时对任何触发方有效。
      const enemy = this.enemies.find((candidate) => (
        candidate.alive && candidate.team !== mine.ownerTeam && distanceSq2D(candidate.model.root.position, mine) <= mine.radius * mine.radius
      ));
      if (enemy) {
        // PvP 中敌人是远程代理：damageTank 内部守卫不扣血，这里只作布雷端的命中演出与移除。
        this.damageTank(enemy, PICKUP_TUNING.mine.damage);
        this.vfx.explosion(TEMP_VECTOR_C.set(mine.x, 0.35, mine.z), this.level.palette.danger, 0.82);
        this.shake = Math.max(this.shake, 0.38);
        this.audio.play('explode', 0, { x: mine.x, z: mine.z });
        this.removeMine(index, true);
        continue;
      }

      // PvP 增补：本地自己踩到敌队地雷——受害者权威扣血（relay 模型），died 经 pvpLastAttacker 归因布雷者。
      // 旧版只扫 enemies，自己的坦克不在其中，联机里地雷对受害者零伤害（触发链断裂）。
      if (this.pvpContext) {
        const self = this.selfTank();
        if (self && self.alive && self.team !== mine.ownerTeam && distanceSq2D(self.model.root.position, mine) <= mine.radius * mine.radius) {
          this.pvpLastAttacker = mine.ownerPlayerId;
          this.damageTank(self, PICKUP_TUNING.mine.damage);
          this.pvpLastAttacker = null;
          this.vfx.explosion(TEMP_VECTOR_C.set(mine.x, 0.35, mine.z), this.level.palette.danger, 0.82);
          this.shake = Math.max(this.shake, 0.38);
          this.audio.play('explode', 0, { x: mine.x, z: mine.z });
          this.removeMine(index, true);
        }
      }
    }
  }

  private removeMine(index: number, exploded: boolean): void {
    const mine = this.mines[index];
    if (!mine) return;
    this.dynamicRoot.remove(mine.root);
    if (!exploded) this.vfx.pickup(TEMP_VECTOR_C.set(mine.x, 0.35, mine.z), this.level.palette.danger);
    const last = this.mines.pop();
    if (last && index < this.mines.length) this.mines[index] = last;
  }

  private updateHazards(delta: number): void {
    for (const hazard of this.hazards) {
      hazard.phase += delta;
      hazard.root.rotation.y += delta * (hazard.kind === 'spark' ? 0.65 : 0.22);
      hazard.root.scale.setScalar(1 + Math.sin(hazard.phase * (hazard.kind === 'spark' ? 3.2 : 1.7)) * 0.035);
    }

    // 同上：固定步 60Hz 热点，复用 scratch 数组避免每步展开分配。
    const tanks = this.activeTanksScratch;
    tanks.length = 0;
    for (const player of this.players) tanks.push(player);
    for (const enemy of this.enemies) tanks.push(enemy);
    for (const tank of tanks) {
      if (!tank.alive) continue;
      const spark = this.hazards.find((hazard) => (
        hazard.kind === 'spark'
        && distanceSq2D(tank.model.root.position, hazard) <= hazard.radius * hazard.radius
      ));
      if (!spark || tank.hazardTimer > 0) continue;
      tank.hazardTimer = 1.15;
      TEMP_VECTOR_C.copy(tank.model.root.position).y = 0.55;
      this.vfx.impact(TEMP_VECTOR_C, this.level.palette.danger, true);
      this.damageTank(tank, Math.max(1, Math.round(spark.strength)));
    }
  }

  private hazardSpeedMultiplierAt(x: number, z: number): number {
    let multiplier = 1;
    for (const hazard of this.hazards) {
      const dx = x - hazard.x;
      const dz = z - hazard.z;
      if (dx * dx + dz * dz > hazard.radius * hazard.radius) continue;
      if (hazard.kind === 'mud') multiplier = Math.min(multiplier, hazard.strength);
      if (hazard.kind === 'spark') multiplier = Math.min(multiplier, 0.86);
    }
    return multiplier;
  }

  private checkEnemyContact(enemy: TankEntity): void {
    if (!enemy.alive || !this.core) return;
    const coreHitDistance = enemy.radius + 1.42;
    if (distanceSq2D(enemy.model.root.position, this.core) <= coreHitDistance * coreHitDistance) {
      if (enemy.kind === 'boss') {
        this.damageCore(1);
        enemy.bodyAngle += Math.PI;
        enemy.model.root.position.z -= 1.2;
        enemy.fireCooldown = 1.2;
      } else {
        this.damageCore(enemy.kind === 'bruiser' ? 2 : 1);
        this.destroyTank(enemy);
      }
      return;
    }

    for (const player of this.players) {
      if (!player.alive) continue;
      const hitDistance = enemy.radius + player.radius - 0.12;
      if (distanceSq2D(enemy.model.root.position, player.model.root.position) <= hitDistance * hitDistance) {
        this.damageTank(player, enemy.kind === 'bruiser' || enemy.kind === 'boss' ? 2 : 1);
        const push = TEMP_VECTOR.set(player.model.root.position.x - enemy.model.root.position.x, 0, player.model.root.position.z - enemy.model.root.position.z);
        if (push.lengthSq() > 0.001) {
          push.normalize();
          this.moveTank(player, push.x * 0.48, push.z * 0.48);
          this.moveTank(enemy, -push.x * 0.32, -push.z * 0.32);
        }
      }
    }
  }

  private damageCore(damage: number): void {
    if (!this.core || this.core.invulnerable > 0 || this.phase !== 'playing') return;
    this.core.health = Math.max(0, this.core.health - damage);
    this.core.invulnerable = 0.42;
    this.core.pulse = 0.4;
    this.vfx.shieldHit(TEMP_VECTOR_C.set(this.core.x, 1.2, this.core.z), 0xffd552);
    this.audio.play('hit', 0, { x: this.core.x, z: this.core.z });
    this.ui.flashImpact(true);
    this.ui.toast('★', '彩虹核心受击', `剩余生命 ${this.core.health} / ${this.core.maxHealth}`);
    this.shake = Math.max(this.shake, 0.45);
  }

  private moveTank(tank: TankEntity, dx: number, dz: number): void {
    if (!tank.alive) return;
    const position = tank.model.root.position;
    const halfWidth = this.level.arena.width / 2 - tank.radius - 0.45;
    const halfDepth = this.level.arena.depth / 2 - tank.radius - 0.45;
    const oldX = position.x;
    const oldZ = position.z;

    const nextX = THREE.MathUtils.clamp(position.x + dx, -halfWidth, halfWidth);
    if (!this.tankCollidesAt(tank, nextX, position.z)) position.x = nextX;
    const nextZ = THREE.MathUtils.clamp(position.z + dz, -halfDepth, halfDepth);
    if (!this.tankCollidesAt(tank, position.x, nextZ)) position.z = nextZ;

    if (position.x === oldX && Math.abs(dx) > 0.001) tank.velocity.x *= -0.18;
    if (position.z === oldZ && Math.abs(dz) > 0.001) tank.velocity.z *= -0.18;
  }

  private tankCollidesAt(tank: TankEntity, x: number, z: number): boolean {
    for (const obstacle of this.obstacles) {
      if (obstacle.active && this.circleIntersectsObstacle(x, z, tank.radius, obstacle)) return true;
    }
    if (this.core) {
      const dx = x - this.core.x;
      const dz = z - this.core.z;
      const minimum = tank.radius + 1.2;
      if (dx * dx + dz * dz < minimum * minimum) return true;
    }
    return false;
  }

  private resolveTankSeparation(): void {
    const tanks = this.activeTanksScratch;
    tanks.length = 0;
    for (const player of this.players) if (player.alive) tanks.push(player);
    for (const enemy of this.enemies) if (enemy.alive) tanks.push(enemy);
    for (let i = 0; i < tanks.length; i += 1) {
      for (let j = i + 1; j < tanks.length; j += 1) {
        const a = tanks[i];
        const b = tanks[j];
        const dx = b.model.root.position.x - a.model.root.position.x;
        const dz = b.model.root.position.z - a.model.root.position.z;
        const distance = Math.hypot(dx, dz);
        const minimum = (a.radius + b.radius) * 0.82;
        if (distance <= 0.001 || distance >= minimum) continue;
        const overlap = (minimum - distance) * 0.5;
        const nx = dx / distance;
        const nz = dz / distance;
        const aX = a.model.root.position.x - nx * overlap;
        const aZ = a.model.root.position.z - nz * overlap;
        const bX = b.model.root.position.x + nx * overlap;
        const bZ = b.model.root.position.z + nz * overlap;
        // 远程坦克由所属客户端快照驱动，本地不推（否则与快照打架产生抖动）；
        // 本地坦克照常受推，保留贴身碰撞手感。
        if (!a.remote && !this.tankCollidesAt(a, aX, aZ)) {
          a.model.root.position.x = aX;
          a.model.root.position.z = aZ;
        }
        if (!b.remote && !this.tankCollidesAt(b, bX, bZ)) {
          b.model.root.position.x = bX;
          b.model.root.position.z = bZ;
        }
      }
    }
  }

  private circleIntersectsObstacle(x: number, z: number, radius: number, obstacle: ObstacleEntity): boolean {
    const cos = Math.cos(-obstacle.rotation);
    const sin = Math.sin(-obstacle.rotation);
    const dx = x - obstacle.x;
    const dz = z - obstacle.z;
    const localX = dx * cos - dz * sin;
    const localZ = dx * sin + dz * cos;
    const closestX = THREE.MathUtils.clamp(localX, -obstacle.width / 2, obstacle.width / 2);
    const closestZ = THREE.MathUtils.clamp(localZ, -obstacle.depth / 2, obstacle.depth / 2);
    const offsetX = localX - closestX;
    const offsetZ = localZ - closestZ;
    return offsetX * offsetX + offsetZ * offsetZ < radius * radius;
  }

  private pointInObstacle(x: number, z: number, obstacle: ObstacleEntity, padding = 0): boolean {
    const cos = Math.cos(-obstacle.rotation);
    const sin = Math.sin(-obstacle.rotation);
    const dx = x - obstacle.x;
    const dz = z - obstacle.z;
    const localX = dx * cos - dz * sin;
    const localZ = dx * sin + dz * cos;
    return Math.abs(localX) <= obstacle.width / 2 + padding && Math.abs(localZ) <= obstacle.depth / 2 + padding;
  }

  private hasLineOfSight(fromX: number, fromZ: number, toX: number, toZ: number): boolean {
    const distance = Math.hypot(toX - fromX, toZ - fromZ);
    const steps = Math.max(2, Math.ceil(distance / 1.1));
    // 索引循环而非 .some(闭包)：本方法在固定步内按敌人×步进高频调用（~10^4 次/秒量级），
    // 每步一个闭包分配是主要 GC 压力源之一（2026-07-26 GL/CPU 插桩定位）。
    for (let step = 1; step < steps; step += 1) {
      const t = step / steps;
      const x = THREE.MathUtils.lerp(fromX, toX, t);
      const z = THREE.MathUtils.lerp(fromZ, toZ, t);
      for (let i = 0; i < this.obstacles.length; i += 1) {
        const obstacle = this.obstacles[i];
        if (obstacle.active && this.pointInObstacle(x, z, obstacle, 0.08)) return false;
      }
    }
    return true;
  }

  private findNearestEnemy(x: number, z: number, maxDistance: number): TankEntity | null {
    let nearest: TankEntity | null = null;
    let best = maxDistance * maxDistance;
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;
      const distance = (enemy.model.root.position.x - x) ** 2 + (enemy.model.root.position.z - z) ** 2;
      if (distance < best) {
        best = distance;
        nearest = enemy;
      }
    }
    return nearest;
  }

  private checkEndConditions(delta: number): void {
    if (this.phase !== 'playing') return;
    // PvP：本地不做胜负判定，只等服务端 match_end（设计文档 §7.5）。
    if (this.pvpContext) return;
    if (this.core && this.core.health <= 0) {
      this.finish(false);
      return;
    }
    const playersOut = this.players.length > 0 && this.players.every((player) => !player.alive && player.lives <= 0);
    if (playersOut) {
      this.finish(false);
      return;
    }
    const allWavesDone = this.wave.index >= this.level.waves.length - 1 && this.wave.queue.length === 0;
    const enemiesGone = this.enemies.every((enemy) => !enemy.alive);
    if (allWavesDone && enemiesGone && this.wave.completionTimer > 1.05) this.finish(true);
    void delta;
  }

  private finish(victory: boolean): void {
    if (this.resultCommitted) return;
    this.resultCommitted = true;
    this.phase = victory ? 'victory' : 'defeat';
    this.input.clear();
    while (this.projectiles.length > 0) this.removeProjectile(this.projectiles.length - 1);
    if (victory) {
      this.score += (this.core?.health ?? 0) * 250 + Math.max(0, 180 - Math.round(this.elapsed)) * 10;
      this.audio.play('win');
      this.vfx.explosion(TEMP_VECTOR_C.set(this.level.corePosition.x, 2.4, this.level.corePosition.z - 2), 0xffd950, 1.2);
    } else {
      this.audio.play('lose');
      this.shake = 0.8;
    }
    const coreHealth = this.core?.health ?? 0;
    const coreMaxHealth = this.core?.maxHealth ?? 1;
    let stars = victory ? 1 : 0;
    if (victory && coreHealth / coreMaxHealth >= 0.5) stars += 1;
    if (victory && coreHealth === coreMaxHealth && this.maxCombo >= 4) stars += 1;
    const summary: ResultSummary = {
      victory,
      score: this.score,
      maxCombo: this.maxCombo,
      coreHealth,
      coreMaxHealth,
      stars,
      levelIndex: this.selection.levelIndex,
    };
    window.setTimeout(() => this.ui.showResult(summary), 650);
  }

  private updateMenu(delta: number): void {
    const selectedLevel = LEVELS[this.ui.getSelection().levelIndex];
    if (selectedLevel.id !== this.level.id) {
      this.clearWorld();
      this.buildMenuScene();
    }
    this.menuTime += delta;
    this.menuTanks.forEach((tank, index) => {
      tank.root.position.y = Math.sin(this.menuTime * 1.7 + index * 1.4) * 0.045;
      tank.root.rotation.y += (index === 0 ? 0.07 : -0.06) * delta;
      tank.turret.rotation.y = Math.sin(this.menuTime * 0.7 + index) * 0.45;
      tank.antennaTip.scale.setScalar(0.09 + Math.sin(this.menuTime * 5 + index) * 0.012);
    });
    this.updateAmbientAnimations(delta);
    const orbit = Math.sin(this.menuTime * 0.2) * 0.8;
    this.camera.position.x = damp(this.camera.position.x, orbit, 1.5, delta);
    this.camera.position.y = damp(this.camera.position.y, this.mobileLike ? 16 : 13.6, 1.5, delta);
    this.camera.position.z = damp(this.camera.position.z, this.mobileLike ? 20.5 : 17.5, 1.5, delta);
    this.cameraTarget.set(0, 0.9, 1.2);
    this.camera.lookAt(this.cameraTarget);
    this.vfx.update(delta);
    this.trails.update(delta);
    this.styler.update(delta);
  }

  private updateAmbientAnimations(delta: number): void {
    if (this.core) {
      this.core.crystal.rotation.z += delta * 0.78;
      this.core.crystal.position.y = 1.68 + Math.sin(this.elapsed * 2.2 + this.menuTime) * 0.1;
      this.core.rings[0].rotation.z += delta * 0.9;
      this.core.rings[1].rotation.y -= delta * 0.75;
      const pulse = 1 + Math.sin((this.elapsed + this.menuTime) * 3.4) * 0.025 + this.core.pulse * 0.12;
      this.core.shield.scale.set(1.0 * pulse, 0.72 * pulse, 1.0 * pulse);
      this.core.shield.material.opacity = this.core.invulnerable > 0 ? 0.26 : 0.11;
      this.coreLight.intensity = 2.0 + Math.sin((this.elapsed + this.menuTime) * 2.7) * 0.35 + this.core.pulse * 2;
    }

    // 复用 scratch 数组，避免每帧 [...players, ...enemies] 展开分配（与 resolveTankSeparation 错峰使用，进入即清空）。
    const allTanks = this.activeTanksScratch;
    allTanks.length = 0;
    for (const player of this.players) allTanks.push(player);
    for (const enemy of this.enemies) allTanks.push(enemy);
    for (const tank of allTanks) {
      if (!tank.alive) continue;
      const hitScale = tank.hitFlash > 0 ? 1 + Math.sin(tank.hitFlash * 90) * 0.055 : 1;
      const bossBase = tank.kind === 'boss' ? 1.12 : 1;
      tank.model.root.scale.setScalar(bossBase * hitScale);
      if (tank.invulnerable > 0 && tank.team === 'player') {
        tank.model.root.visible = Math.floor(tank.invulnerable * 12) % 2 === 0 || tank.shieldTimer > 0;
      } else {
        tank.model.root.visible = true;
      }
    }
  }

  private updateCamera(delta: number): void {
    // PvP 2v2：相机只框本地坦克——远程队友拉到大地图另一侧时，取景中点会把自己挤到屏幕边缘。
    // 本地坦克阵亡等待重生时退回框存活队友（PvE 无 remote 标记，行为不变）。复用专属 scratch 零分配。
    const activePlayers = this.cameraTanksScratch;
    activePlayers.length = 0;
    for (const player of this.players) if (player.alive && !player.remote) activePlayers.push(player);
    if (activePlayers.length === 0) {
      for (const player of this.players) if (player.alive) activePlayers.push(player);
    }
    if (activePlayers.length === 0) {
      this.desiredCameraTarget.set(this.core?.x ?? 0, 0.6, this.core?.z ?? 0);
    } else {
      let x = 0;
      let z = 0;
      for (const player of activePlayers) {
        x += player.model.root.position.x;
        z += player.model.root.position.z;
      }
      x /= activePlayers.length;
      z /= activePlayers.length;
      const nearest = this.findNearestEnemy(x, z, 12);
      if (nearest) {
        x = THREE.MathUtils.lerp(x, nearest.model.root.position.x, 0.16);
        z = THREE.MathUtils.lerp(z, nearest.model.root.position.z, 0.12);
      }
      if (this.core) {
        x = THREE.MathUtils.lerp(x, this.core.x, 0.12);
        z = THREE.MathUtils.lerp(z, this.core.z, 0.18);
      }
      this.desiredCameraTarget.set(x, 0.55, z);
    }

    this.cameraTarget.lerp(this.desiredCameraTarget, 1 - Math.exp(-3.3 * delta));
    // 空间音听者跟随相机焦点（≈玩家注意力中心）；菜单/结算阶段也无害。
    this.audio.setListener(this.cameraTarget.x, this.cameraTarget.z);
    let spread = 0;
    if (activePlayers.length > 1) spread = Math.sqrt(distanceSq2D(activePlayers[0].model.root.position, activePlayers[1].model.root.position));
    const baseHeight = this.mobileLike ? 29.5 : 23.5;
    const baseBack = this.mobileLike ? 22.2 : 18.2;
    const zoom = Math.min(4.5, spread * 0.24);
    const desired = TEMP_VECTOR.set(this.cameraTarget.x * 0.16, baseHeight + zoom, this.cameraTarget.z + baseBack + zoom * 0.65);
    this.shake = Math.max(0, this.shake - delta * 1.9);
    if (!this.reducedMotion && this.shake > 0.001) {
      const amount = this.shake * this.shake;
      desired.x += (Math.random() - 0.5) * amount;
      desired.y += (Math.random() - 0.5) * amount * 0.35;
      desired.z += (Math.random() - 0.5) * amount;
    }
    this.camera.position.lerp(desired, 1 - Math.exp(-4.2 * delta));
    this.camera.lookAt(this.cameraTarget);
  }

  private updateHud(force: boolean): void {
    const now = performance.now();
    if (force || now - this.lastHudUpdate > 80) {
      this.ui.update(this.getSnapshot());
      this.lastHudUpdate = now;
    }
    if (force || now - this.lastRadarUpdate > 120) {
      // PvP 迷雾：雷达同步过滤——敌坦克只显示当前可见的，拾取物/地雷只显示已探索区域的。
      const fog = this.fog;
      const radarData: RadarData = {
        arenaWidth: this.level.arena.width,
        arenaDepth: this.level.arena.depth,
        core: this.core ? { x: this.core.x, z: this.core.z } : null,
        players: this.players.filter((player) => player.alive).map((player) => ({ x: player.model.root.position.x, z: player.model.root.position.z })),
        enemies: this.enemies
          .filter((enemy) => enemy.alive && (!fog || fog.isVisible(enemy.model.root.position.x, enemy.model.root.position.z)))
          .map((enemy) => ({ x: enemy.model.root.position.x, z: enemy.model.root.position.z })),
        pickups: [
          ...this.pickups.filter((pickup) => !fog || fog.isExplored(pickup.x, pickup.z)).map((pickup) => ({ x: pickup.x, z: pickup.z })),
          ...this.mines.filter((mine) => !fog || fog.isExplored(mine.x, mine.z)).map((mine) => ({ x: mine.x, z: mine.z })),
        ],
      };
      this.ui.renderRadar(radarData);
      this.lastRadarUpdate = now;
    }
  }

  private updateDebugPanel(): void {
    if (!this.debugVisible) {
      if (this.debugPanelWasVisible) {
        this.ui.setDebug('', false);
        this.debugPanelWasVisible = false;
      }
      return;
    }
    const now = performance.now();
    if (now - this.lastDebugUpdate < 250) return;
    this.lastDebugUpdate = now;
    this.debugPanelWasVisible = true;
    const snapshot = this.getSnapshot();
    const text = [
      'TOON TANK DIAGNOSTICS',
      `phase: ${snapshot.phase}`,
      `level: ${snapshot.levelIndex + 1} / mode: ${snapshot.mode} / difficulty: ${snapshot.difficulty}`,
      `fps: ${snapshot.fps.toFixed(1)} / fixed step: ${(FIXED_STEP * 1000).toFixed(2)}ms`,
      `renderer: ${snapshot.renderer.calls} calls / ${snapshot.renderer.triangles.toLocaleString()} tris`,
      `resources: ${snapshot.renderer.geometries} geo / ${snapshot.renderer.textures} tex`,
      `entities: ${snapshot.players.length} players / ${snapshot.enemies} enemies / ${snapshot.projectiles} shells / ${snapshot.pickups} pickups / ${snapshot.mines} mines`,
      `vfx: ${this.vfx.activeCount} / obstacles: ${this.obstacles.filter((obstacle) => obstacle.active).length} / hazards: ${snapshot.hazards} / pads: ${snapshot.pickupSpots}`,
      `score: ${snapshot.score} / combo: x${snapshot.combo} / defeated: ${snapshot.defeated}`,
      `core: ${snapshot.coreHealth}/${snapshot.coreMaxHealth} / elapsed: ${snapshot.elapsed.toFixed(1)}s`,
      `quality: ${this.lowSpec ? 'adaptive-low' : this.mobileLike ? 'mobile' : 'high'} / DPR: ${this.renderer.getPixelRatio().toFixed(2)} / shadows: ${this.shadowsEnabled ? 'on' : 'off'}`,
      `simulation catch-up drops: ${this.droppedSimulationFrames}`,
      `canvas: ${this.canvas.width}×${this.canvas.height}`,
    ].join('\n');
    this.ui.setDebug(text, true);
  }

  private updateFps(delta: number): void {
    this.fpsAccumulator += delta;
    this.fpsFrames += 1;
    if (this.fpsAccumulator >= 0.75) {
      this.fps = this.fpsFrames / this.fpsAccumulator;
      this.fpsAccumulator = 0;
      this.fpsFrames = 0;
      if (this.phase !== 'playing' || this.lowSpec) return;
      // 降档：连续 2 个窗口 <48fps（先关阴影，再阶梯降 renderScale）。
      this.lowFpsWindows = this.fps < 48 ? this.lowFpsWindows + 1 : Math.max(0, this.lowFpsWindows - 1);
      // 回升：连续 4 个窗口 ≥57fps；阈值与 <48 之间留出迟滞带，且升速(+0.06/窗口)慢于降速(-0.12)，避免振荡。
      this.highFpsWindows = this.fps >= 57 ? this.highFpsWindows + 1 : 0;
      if (this.lowFpsWindows >= 2) {
        this.lowFpsWindows = 0;
        this.highFpsWindows = 0;
        if (this.shadowsEnabled) {
          this.shadowsEnabled = false;
          this.adaptiveShadowDrop = true;
          this.renderer.shadowMap.enabled = false;
          this.keyLight.castShadow = false;
        } else if (this.renderScale > 0.72) {
          this.renderScale = Math.max(0.72, this.renderScale - 0.12);
          this.applyRenderResolution();
          this.vfx.setQualityScale(this.mobileLike ? 0.48 : 0.66);
          this.trails.setQualityScale(this.mobileLike ? 0.48 : 0.66);
          this.styler.setQualityScale(this.mobileLike ? 0.48 : 0.66);
        }
        return;
      }
      // 回升仅针对被自适应降过的档位（阴影初始就关的设备不碰）；lowSpec 已在上方返回。
      if (this.highFpsWindows < 4 || (this.renderScale >= 1 && !this.adaptiveShadowDrop)) return;
      this.highFpsWindows = 0;
      if (this.renderScale < 1) {
        this.renderScale = Math.min(1, this.renderScale + 0.06);
        this.applyRenderResolution();
        if (this.renderScale >= 1) {
          this.vfx.setQualityScale(this.mobileLike ? 0.62 : 0.86);
          this.trails.setQualityScale(this.mobileLike ? 0.62 : 0.86);
          this.styler.setQualityScale(this.mobileLike ? 0.62 : 0.86);
        }
      } else {
        this.adaptiveShadowDrop = false;
        this.shadowsEnabled = true;
        this.renderer.shadowMap.enabled = true;
        this.keyLight.castShadow = true;
      }
    }
  }

  private removeProjectile(index: number): void {
    const projectile = this.projectiles[index];
    if (!projectile) return;
    this.dynamicRoot.remove(projectile.root);
    projectile.root.visible = false;
    projectile.root.position.set(0, -100, 0);
    projectile.root.rotation.set(0, 0, 0);
    if (this.projectilePool[projectile.poolKey].length < 48) this.projectilePool[projectile.poolKey].push(projectile);
    const last = this.projectiles.pop();
    if (last && index < this.projectiles.length) this.projectiles[index] = last;
  }

  private clearWorld(): void {
    // PvP 迷雾：先摘除 overlay 并释放其资源，再整枝 dispose（避免双重销毁）。
    this.fog?.dispose();
    this.fog = null;
    this.vfx.clear();
    // 拖尾/印痕随世界清空（实例长驻不 dispose，同 vfx 惯例）。
    this.trails.clear();
    // PvP 胜利演出：释放实例表面与残留粒子（quitToMenu/开新局/rematch 均经过这里）。
    this.victoryFx?.dispose();
    this.victoryFx = null;
    while (this.projectiles.length > 0) this.removeProjectile(this.projectiles.length - 1);
    const disposeBranch = (root: any): void => {
      const geometries = new Set<any>();
      const materials = new Set<any>();
      root.traverse((object: any) => {
        if (object.geometry) geometries.add(object.geometry);
        const candidate = object.material;
        if (Array.isArray(candidate)) candidate.forEach((material) => materials.add(material));
        else if (candidate) materials.add(candidate);
      });
      geometries.forEach((geometry) => {
        if (!geometry.userData?.persistent) geometry.dispose?.();
      });
      materials.forEach((material) => {
        if (material.userData?.persistent) return;
        for (const key of ['map', 'alphaMap', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap']) {
          const texture = material[key];
          if (texture && !texture.userData?.persistent) texture.dispose?.();
        }
        material.dispose?.();
      });
    };
    disposeBranch(this.decorRoot);
    disposeBranch(this.gameplayRoot);
    disposeBranch(this.dynamicRoot);
    this.decorRoot.clear();
    this.gameplayRoot.clear();
    this.dynamicRoot.clear();
    this.players = [];
    this.enemies = [];
    this.enemyReclaimQueue.length = 0;
    this.activeTanksScratch.length = 0;
    this.syncTanksScratch.length = 0;
    this.cameraTanksScratch.length = 0;
    this.obstacles = [];
    this.pickups = [];
    this.pickupSpots = [];
    this.hazards = [];
    this.mines = [];
    this.pickupSpawnTimer = 0;
    this.core = null;
    this.menuTanks = [];
  }

  private playerPalette(index: number): TankPalette {
    return index === 0
      ? { primary: 0xffd34f, secondary: 0x4cc9c0, trim: 0xfff4d3, dark: 0x25384c, signal: 0xff8a45, glass: 0x9beaff }
      : { primary: 0x58dff4, secondary: 0xee6da8, trim: 0xfff1dc, dark: 0x27354d, signal: 0x6affc6, glass: 0xb4f4ff };
  }

  private enemyPalette(kind: EnemyKind): TankPalette {
    const palettes: Record<EnemyKind, TankPalette> = {
      scout: { primary: 0xff675d, secondary: 0xf3ad4e, trim: 0xffe4bc, dark: 0x3e2f44, signal: 0xffe04c, glass: 0xffbdd5 },
      bruiser: { primary: 0x9e5de0, secondary: 0xff765f, trim: 0xffcb56, dark: 0x342d4d, signal: 0xff477a, glass: 0xe6b8ff },
      sniper: { primary: 0xe4578f, secondary: 0x536ee6, trim: 0x62ebea, dark: 0x292e51, signal: 0x65f5ff, glass: 0xffb8dc },
      boss: { primary: 0x6d3d9e, secondary: 0xe24f7c, trim: 0xffd54e, dark: 0x25203c, signal: 0xffef5c, glass: 0xc9a9ff },
    };
    return palettes[kind];
  }

  private resize = (): void => {
    this.mobileLikeValue = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 760;
    const width = Math.max(1, this.canvas.clientWidth || window.innerWidth);
    const height = Math.max(1, this.canvas.clientHeight || window.innerHeight);
    this.camera.aspect = width / height;
    this.camera.fov = this.mobileLike ? 55 : 48;
    this.camera.updateProjectionMatrix();
    this.applyRenderResolution(width, height);
  };

  private shouldUseRealtimeShadows(): boolean {
    const cores = Number(navigator.hardwareConcurrency || 4);
    const memory = Number((navigator as any).deviceMemory || 4);
    return !this.lowSpec && !this.mobileLike && cores >= 6 && memory >= 4;
  }

  private applyRenderResolution(width = Math.max(1, this.canvas.clientWidth || window.innerWidth), height = Math.max(1, this.canvas.clientHeight || window.innerHeight)): void {
    const deviceRatio = window.devicePixelRatio || 1;
    const cap = this.lowSpec ? 1 : this.mobileLike ? 1.18 : 1.48;
    const pixelRatio = Math.max(0.78, Math.min(deviceRatio, cap) * this.renderScale);
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(width, height, false);
  }

  private exposeDiagnostics(): void {
    const performanceInfo = () => {
      let lights = 0;
      let pointLights = 0;
      this.scene.traverse((object: any) => {
        if (object.isLight) lights += 1;
        if (object.isPointLight) pointLights += 1;
      });
      return {
        ...this.renderer.info.render,
        ...this.renderer.info.memory,
        programs: Array.isArray(this.renderer.info.programs) ? this.renderer.info.programs.length : 0,
        lights,
        pointLights,
        pixelRatio: this.renderer.getPixelRatio(),
        shadowsEnabled: this.shadowsEnabled,
        droppedSimulationFrames: this.droppedSimulationFrames,
        shaderPrewarm: {
          durationMs: this.lastShaderPrewarmMs,
          failures: this.shaderPrewarmFailures,
        },
        audioUnlockFailures: this.audioUnlockFailures,
        projectiles: this.projectiles.length,
        projectilePool: {
          playerNormal: this.projectilePool['player-normal'].length,
          enemyNormal: this.projectilePool['enemy-normal'].length,
          enemyStrong: this.projectilePool['enemy-strong'].length,
        },
        vfx: this.vfx.diagnostics,
      };
    };
    const api = {
      snapshot: () => this.getSnapshot(),
      startGame: (selection?: Partial<GameSelection>) => this.startGame({ ...this.selection, ...selection }),
      pause: () => this.pauseGame(),
      resume: () => this.resumeGame(),
      restart: () => this.restartGame(),
      quit: () => this.quitToMenu(),
      forceVictory: () => this.forceVictory(),
      forceDefeat: () => this.forceDefeat(),
      spawnPickup: (kind?: PickupKind, targetX?: number, targetZ?: number) => {
        const player = this.players.find((candidate) => candidate.alive);
        if (!player) return false;
        const x = Number.isFinite(targetX) ? Number(targetX) : player.model.root.position.x + 1.4;
        const z = Number.isFinite(targetZ) ? Number(targetZ) : player.model.root.position.z;
        return this.spawnPickupAt(x, z, kind);
      },
      step: (seconds = 1, renderFrame = false) => {
        this.camera.updateMatrixWorld(true);
        const steps = Math.max(1, Math.min(3600, Math.ceil(Number(seconds) / FIXED_STEP)));
        for (let index = 0; index < steps && this.phase === 'playing'; index += 1) {
          this.fixedUpdate(FIXED_STEP);
          this.vfx.update(FIXED_STEP);
          // 拖尾/氛围随诊断步进一起 tick：步进截图能看到尘埃状态，拖尾泡也不会只攒不消。
          this.trails.update(FIXED_STEP);
          this.styler.update(FIXED_STEP);
          this.updateAmbientAnimations(FIXED_STEP);
        }
        this.updateCamera(FIXED_STEP);
        this.camera.updateMatrixWorld(true);
        this.updateHud(true);
        if (!this.loopEnabled && renderFrame) this.renderer.render(this.scene, this.camera);
        return this.getSnapshot();
      },
      entities: () => ({
        players: this.players.map((player) => ({
          id: player.id,
          index: player.playerIndex,
          alive: player.alive,
          x: player.model.root.position.x,
          y: player.model.root.position.y,
          z: player.model.root.position.z,
          bodyAngle: player.bodyAngle,
          turretAngle: player.turretAngle,
          health: player.health,
          lives: player.lives,
          shieldTimer: player.shieldTimer,
          rapidTimer: player.rapidTimer,
          overdriveTimer: player.overdriveTimer,
          droneTimer: player.droneTimer,
          inputIntensity: player.inputIntensity,
          speedRatio: player.speedRatio,
          handlingLean: player.handlingLean,
          handlingPitch: player.handlingPitch,
          recoil: player.recoil,
        })),
        enemies: this.enemies.filter((enemy) => enemy.alive).map((enemy) => ({
          id: enemy.id,
          kind: enemy.kind,
          x: enemy.model.root.position.x,
          y: enemy.model.root.position.y,
          z: enemy.model.root.position.z,
          health: enemy.health,
          maxHealth: enemy.maxHealth,
          droneTimer: enemy.droneTimer,
          visible: enemy.model.root.visible,
        })),
        projectiles: this.projectiles.map((projectile) => ({
          id: projectile.id,
          team: projectile.team,
          ownerId: projectile.ownerId,
          x: projectile.root.position.x,
          y: projectile.root.position.y,
          z: projectile.root.position.z,
          vx: projectile.velocity.x,
          vy: projectile.velocity.y,
          vz: projectile.velocity.z,
          speed: projectile.velocity.length(),
          age: projectile.age,
          bounces: projectile.bounces,
          poolKey: projectile.poolKey,
        })),
      }),
      aimPose: (playerIndex = 0) => {
        const player = this.players[playerIndex];
        if (!player?.alive) return null;
        this.getMuzzlePose(player, TEMP_VECTOR_C, TEMP_VECTOR_B);
        return {
          muzzle: { x: TEMP_VECTOR_C.x, y: TEMP_VECTOR_C.y, z: TEMP_VECTOR_C.z },
          direction: { x: TEMP_VECTOR_B.x, y: TEMP_VECTOR_B.y, z: TEMP_VECTOR_B.z },
          bodyAngle: player.bodyAngle,
          turretAngle: player.turretAngle,
        };
      },
      firePlayer: (playerIndex = 0) => {
        const player = this.players[playerIndex];
        if (!player?.alive || this.phase !== 'playing') return [];
        const start = this.projectiles.length;
        player.fireCooldown = 0;
        this.fireTank(player);
        return this.projectiles.slice(start).map((projectile) => projectile.id);
      },
      fireEnemy: (kind: EnemyKind = 'scout') => {
        let enemy = this.enemies.find((candidate) => candidate.alive && candidate.kind === kind);
        if (!enemy) enemy = this.spawnEnemy(kind, undefined, true);
        const start = this.projectiles.length;
        enemy.fireCooldown = 0;
        this.fireTank(enemy);
        return this.projectiles.slice(start).map((projectile) => projectile.id);
      },
      clearProjectiles: () => {
        while (this.projectiles.length > 0) this.removeProjectile(this.projectiles.length - 1);
        return performanceInfo();
      },
      clearEffects: () => {
        this.vfx.clear();
        return performanceInfo();
      },
      spawnEnemy: (kind: EnemyKind = 'scout', x?: number, z?: number) => this.spawnEnemy(kind, Number.isFinite(x) && Number.isFinite(z) ? { x: Number(x), z: Number(z) } : undefined, true),
      levelData: () => ({
        arenaWidth: this.level.arena.width,
        arenaDepth: this.level.arena.depth,
        obstacles: this.level.obstacles.length,
        hazards: this.level.hazards.length,
        pickupSpots: this.level.pickupSpots.length,
        pickupKinds: Array.from(new Set(this.level.pickupSpots.flatMap((spot) => spot.kinds))).sort(),
        activeMines: this.mines.length,
        activePickups: this.pickups.map((pickup) => ({
          kind: pickup.kind,
          label: PICKUP_INFO[pickup.kind].label,
          effect: PICKUP_INFO[pickup.kind].effect,
          model: PICKUP_INFO[pickup.kind].model,
          x: pickup.x,
          z: pickup.z,
        })),
      }),
      setDebug: (value: boolean) => { this.debugVisible = value; },
      setLoopEnabled: (value: boolean) => {
        this.loopEnabled = Boolean(value);
        this.accumulator = 0;
        this.clock.getDelta();
        return this.loopEnabled;
      },
      performanceInfo,
      rendererInfo: performanceInfo,
      renderOnce: () => {
        this.renderer.render(this.scene, this.camera);
        return this.getSnapshot();
      },
      fixedStep: FIXED_STEP,
      // PvP 联调钩子：仅联机对局中生效（PvE 返回 false）。供 scripts/pvp_e2e.py 使用。
      pvpTeleportSelf: (x: number, z: number) => {
        const self = this.selfTank();
        if (!this.pvpContext || !self) return false;
        self.model.root.position.set(Number(x), 0, Number(z));
        self.velocity.set(0, 0, 0);
        return true;
      },
      pvpFogVisible: (x: number, z: number) => (this.fog ? this.fog.isVisible(Number(x), Number(z)) : null),
      version: '1.0.1',
    };
    (window as any).__THREE_GAME_DIAGNOSTICS__ = api;
    (window as any).__TOON_TANK_GAME__ = api;
  }

  private get mobileLike(): boolean {
    return this.mobileLikeValue;
  }
}
